import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/helpers';

// ── Product → Category mapping (lowercase title → category) ──
const PRODUCT_TITLE_CATEGORY_MAP = {
  'hybrid latex mattress': 'Mattresses', 'latex ortho classic': 'Mattresses', 'original mattress': 'Mattresses',
  'ultima memory foam': 'Mattresses', 'ultima natural latex': 'Mattresses', 'cloud mattress': 'Mattresses',
  'tri-fold mattress': 'Mattresses', 'baby mattress': 'Mattresses', 'baby natural latex mattress': 'Mattresses',
  "the founder's mattress": 'Founders',
  'memory foam mattress topper': 'Mattress Topper', 'natural latex mattress topper': 'Mattress Topper',
  'aeroflow foam mattress topper': 'Mattress Topper', 'microfiber mattress topper': 'Mattress Topper',
  'mattress protector': 'Mattress Protector', 'quilted mattress protector': 'Mattress Protector',
  'mattress cover': 'Mattress Cover',
  'softtouch memory foam pillow': 'Pillow', 'contour softtouch memory foam pillow': 'Pillow',
  'memory foam slim pillow': 'Pillow', 'contour cooltec memory foam pillow': 'Pillow',
  'cooltec memory foam pillow': 'Pillow', 'marshmallow pillow': 'Pillow', 'cloud pillow': 'Pillow',
  'fiber lite pillow': 'Pillow', 'microfiber slim pillow': 'Pillow', 'cuddle pillow': 'Pillow',
  'grid pillow': 'Pillow', 'natural latex pillow': 'Pillow', 'contour natural latex pillow': 'Pillow',
  'dual comfort pillow': 'Pillow', 'hybrid pillow': 'Pillow', 'travel neck pillow': 'Pillow',
  '300 tc sateen fitted bedsheet': 'Sheet & Cover', '200 tc percale fitted bedsheet set': 'Sheet & Cover',
  'jersey fitted bedsheet': 'Sheet & Cover', '300 tc sateen duvet cover': 'Sheet & Cover',
  '200 tc percale duvet cover': 'Sheet & Cover', 'jersey duvet cover': 'Sheet & Cover',
  'summer comforter': 'Comforter', 'all season comforter': 'Comforter',
  'all season reversible comforter': 'Comforter', 'reversible comforter': 'Comforter',
  '300 gsm winter luxe comforter': 'Comforter', '150 gsm summer luxe comforter': 'Comforter', 'winter comforter': 'Comforter',
  'comforter': 'Comforter',
  'weighted blanket': 'Weighted Blanket',
  'muslin throw': 'Throw', 'knitted throw': 'Throw',
  '300 tc sateen pillow cases': 'Pillow Cases', '200 tc percale pillow cases': 'Pillow Cases',
  'jersey pillow cases': 'Pillow Cases', 'satin pillow cases': 'Pillow Cases',
  'slim seat cushion': 'Mobility', 'pro back cushion': 'Mobility', 'pro seat cushion': 'Mobility',
  'pro neck pillow': 'Mobility', 'slim back cushion': 'Mobility', 'wedge pillow': 'Mobility',
  'lumbar seat cushion': 'Mobility', 'car neck pillow': 'Mobility', 'pro car bundle': 'Mobility',
  'ergo chair bundle': 'Mobility', 'car bundle': 'Mobility',
  'dog bed - original': 'Dog Bed', 'dog bed - orthopedic': 'Dog Bed',
  'dog bed cover - original': 'Dog Bed', 'dog bed cover - orthopedic': 'Dog Bed',
  'dog bed cover': 'Dog Bed', 'dog bed': 'Dog Bed',
  'taurus smart recliner bed': 'Bed', 'katachi bed': 'Bed', 'ohayo bed': 'Bed',
  'enso recliner': 'Recliner', 'seika recliner': 'Recliner',
  'tote bag': 'Others',
};

// ── Category → HSN code + GST rate ──
const CATEGORY_TAX_MAP = {
  'Mattresses':         { hsn: '94042100', gst: 18 },
  'Founders':           { hsn: '94042100', gst: 18 },
  'Mattress Topper':    { hsn: '94042100', gst: 18 },
  'Mattress Protector': { hsn: '63023100', gst: 12 },
  'Mattress Cover':     { hsn: '63023100', gst: 12 },
  'Pillow':             { hsn: '94049000', gst: 18 },
  'Sheet & Cover':      { hsn: '63023100', gst: 12 },
  'Comforter':          { hsn: '94049000', gst: 18 },
  'Weighted Blanket':   { hsn: '94049000', gst: 18 },
  'Throw':              { hsn: '63014000', gst: 12 },
  'Pillow Cases':       { hsn: '63023100', gst: 12 },
  'Mobility':           { hsn: '94049000', gst: 18 },
  'Dog Bed':            { hsn: '94049000', gst: 18 },
  'Bed':                { hsn: '94035000', gst: 28 },
  'Recliner':           { hsn: '94016100', gst: 28 },
  'Others':             { hsn: '59039090', gst: 18 },
};

/** Look up HSN + GST for a product title. Returns { hsn, gst } */
function getProductTax(title) {
  const DEFAULT = { hsn: '', gst: 18 };
  if (!title) return DEFAULT;
  const lower = title.toLowerCase().trim();
  // Exact match
  if (PRODUCT_TITLE_CATEGORY_MAP[lower]) {
    return CATEGORY_TAX_MAP[PRODUCT_TITLE_CATEGORY_MAP[lower]] || DEFAULT;
  }
  // Substring match
  for (const [key, cat] of Object.entries(PRODUCT_TITLE_CATEGORY_MAP)) {
    if (lower.includes(key)) return CATEGORY_TAX_MAP[cat] || DEFAULT;
  }
  return DEFAULT;
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('customer');
  const [otherReasonText, setOtherReasonText] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [, setTick] = useState(0);

  // Date filter — default to today
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [truncated, setTruncated] = useState(false);
  const [activePreset, setActivePreset] = useState('today');

  const DATE_PRESETS = [
    { key: 'today',   label: 'Today',     days: 0 },
    { key: '7d',      label: 'Last 7d',   days: 6 },
    { key: '30d',     label: 'Last 30d',  days: 29 },
    { key: '90d',     label: 'Last 90d',  days: 89 },
  ];

  const applyPreset = (preset) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - preset.days);
    const fmt = d => d.toISOString().slice(0, 10);
    setDateFrom(fmt(from));
    setDateTo(fmt(to));
    setActivePreset(preset.key);
  };

  // Tick every second to update cancel countdown timer (DISABLED — will activate later)
  // useEffect(() => {
  //   if (!selectedOrder || selectedOrder.cancelledAt) return;
  //   const interval = setInterval(() => setTick(t => t + 1), 1000);
  //   return () => clearInterval(interval);
  // }, [selectedOrder]);

  useEffect(() => {
    fetchOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/checkout/orders', {
        params: { limit: 250, dateFrom, dateTo }
      });
      setOrders(response.data.orders);
      setTruncated(!!response.data.truncated);
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  // Client-side search
  const filteredOrders = orders.filter(order => {
    if (!searchQuery || searchQuery.length < 2) return true;
    const q = searchQuery.toLowerCase();
    const customerName = order.customer ? order.customer.name.toLowerCase() : '';
    const customerEmail = order.customer ? (order.customer.email || '').toLowerCase() : '';
    const orderNum = String(order.orderNumber);
    const orderName = (order.name || '').toLowerCase();
    const status = (order.financialStatus || '').toLowerCase();
    const fulfillment = (order.fulfillmentStatus || '').toLowerCase();
    const total = String(order.total);
    return customerName.includes(q) || customerEmail.includes(q) || orderNum.includes(q) || orderName.includes(q) || status.includes(q) || fulfillment.includes(q) || total.includes(q);
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedOrders = filteredOrders.slice(startIdx, startIdx + pageSize);

  // Reset to page 1 when search or page size changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    const dataToExport = filteredOrders;
    if (dataToExport.length === 0) return;

    const headers = ['Order Name', 'Order #', 'Date', 'Customer', 'Email', 'Phone', 'Payment Status', 'Fulfillment Status', 'Items', 'Subtotal', 'Tax', 'Total', 'Tags'];
    const rows = dataToExport.map(order => {
      const customerName = order.customer ? order.customer.name || '' : 'Walk-in';
      const email = order.customer?.email || '';
      const phone = order.customer?.phone || '';
      const items = (order.lineItems || []).map(li => `${li.title} x${li.quantity}`).join('; ');
      return [
        order.name || '',
        order.orderNumber,
        new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        customerName,
        email,
        phone,
        order.financialStatus || '',
        order.cancelledAt ? 'Cancelled' : order.fulfillmentStatus || 'Unfulfilled',
        items,
        order.subtotal || '',
        order.totalTax || '',
        order.total || '',
        order.tags || ''
      ];
    });

    const escapeCSV = (val) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const csv = [headers.map(escapeCSV).join(','), ...rows.map(r => r.map(escapeCSV).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `POS-Orders-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const viewOrderDetails = async (orderId) => {
    try {
      setShowCancelConfirm(false);
      setCancelResult(null);
      const response = await api.get(`/checkout/order/${orderId}`);
      setSelectedOrder(response.data.order);
    } catch (error) {
      console.error('Error fetching order details:', error);
      alert('Failed to fetch order details');
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;
    setCancelling(true);
    setCancelResult(null);
    try {
      const response = await api.post(`/checkout/order/${selectedOrder.id}/cancel`, {
        reason: cancelReason,
        otherReason: cancelReason === 'other' ? otherReasonText : undefined
      });
      setCancelResult({
        success: true,
        refundType: response.data.refundType,
        refundMessage: response.data.refundMessage,
        message: response.data.message,
        cancelledFulfillments: response.data.cancelledFulfillments
      });
      // Refresh the selected order detail to show updated status
      const updated = response.data.order;
      setSelectedOrder(prev => ({
        ...prev,
        financialStatus: updated.financialStatus === 'paid' ? 'voided' : updated.financialStatus,
        fulfillmentStatus: null,
        cancelReason: updated.cancelReason,
        cancelledAt: updated.cancelledAt
      }));
      fetchOrders();
    } catch (error) {
      const errMsg = error.response?.data?.error || 'Failed to cancel order';
      setCancelResult({ success: false, message: errMsg });
    } finally {
      setCancelling(false);
    }
  };

  // Check if order is within 10-minute cancellation window
  const isCancellable = (order) => {
    if (!order || order.cancelledAt) return false;
    const orderAge = Date.now() - new Date(order.createdAt).getTime();
    return orderAge <= 10 * 60 * 1000;
  };

  const getCancelTimeRemaining = (order) => {
    if (!order) return '';
    const orderAge = Date.now() - new Date(order.createdAt).getTime();
    const remaining = 10 * 60 * 1000 - orderAge;
    if (remaining <= 0) return 'expired';
    const mins = Math.floor(remaining / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  // Get payment method from note attributes
  const getPaymentMethod = (order) => {
    if (!order?.noteAttributes) return '';
    const attr = order.noteAttributes.find(a => a.name === 'Payment Method');
    return attr?.value || '';
  };

  // Classify refund type from payment method
  const getRefundInfo = (paymentMethod) => {
    const pm = (paymentMethod || '').toLowerCase();
    if (pm.includes('cash')) {
      return { type: 'manual', label: 'Cash refund — please hand over the refund amount to the customer directly.', color: '#dc2626' };
    }
    if (pm.includes('split') || pm.includes('upi') || pm.includes('card')) {
      return { type: 'pending', label: 'Online refund will be processed shortly. The customer will receive it in their original payment account.', color: '#d97706' };
    }
    if (pm.includes('net') || pm.includes('district') || pm.includes('snapmint') || pm.includes('bank')) {
      return { type: 'manual', label: 'Refund needs to be processed manually — please coordinate with the customer for their account details.', color: '#dc2626' };
    }
    return { type: 'manual', label: 'Refund needs to be processed manually — please coordinate with the customer.', color: '#dc2626' };
  };

  const handlePrintReceipt = () => {
    if (!selectedOrder) return;
    const o = selectedOrder;
    const isWarehouse = (o.tags || '').toLowerCase().includes('warehouse');

    // --- PDF toolbar + auto-download script (injected into the invoice window) ---
    // Loads html2pdf.js from CDN, shows a floating toolbar with Download PDF / Print / Close buttons.
    // Also uploads the generated PDF to Shopify Files + posts a timeline comment so
    // the invoice is permanently attached to the Shopify order timeline.
    // Note: html2pdf only captures `table.outer` — toolbar never appears in the PDF.
    const invoiceActionsBar = (filename, orderId, invoiceType) => {
      // Bearer token for the popup's API call (same-origin localStorage)
      let bearer = '';
      try {
        const raw = localStorage.getItem('auth-storage');
        if (raw) bearer = (JSON.parse(raw)?.state?.token) || '';
      } catch (_) {}
      return `
<style>
  @media print { #invoice-actions { display:none !important; } }
  #invoice-actions button:hover { opacity: 0.85; }
  #invoice-status { font-size:11px; color:#6b7280; margin-top:6px; text-align:right }
  #invoice-status.success { color:#166534; font-weight:600 }
  #invoice-status.error { color:#dc2626; font-weight:600 }
</style>
<div id="invoice-actions" style="position:fixed;top:12px;right:12px;z-index:2147483647;display:flex;flex-direction:column;align-items:flex-end;gap:4px;background:#ffffff;padding:10px 12px;border:2px solid #c88346;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,0.25);font-family:Arial,sans-serif">
  <div style="display:flex;gap:8px">
    <button id="btn-print" type="button" style="padding:9px 16px;background:#3d2b1f;color:#fff;border:none;border-radius:6px;cursor:pointer;font-weight:700;font-size:13px">🖨 Print</button>
    <button id="btn-close" type="button" style="padding:9px 16px;background:#eeeeee;color:#333;border:none;border-radius:6px;cursor:pointer;font-weight:700;font-size:13px">✕ Close</button>
  </div>
  <div id="invoice-status"></div>
</div>
<script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
<script>
(function(){
  var FILENAME = ${JSON.stringify(filename)};
  var ORDER_ID = ${JSON.stringify(String(orderId || ''))};
  var INVOICE_TYPE = ${JSON.stringify(invoiceType || '')};
  var BEARER = ${JSON.stringify(bearer)};
  var uploaded = false;
  function setStatus(msg, cls) {
    var el = document.getElementById('invoice-status');
    if (!el) return;
    el.textContent = msg;
    el.className = cls || '';
  }
  function getTarget() { return document.querySelector('table.outer'); }
  function pdfOptions() {
    var el = getTarget();
    return {
      margin: [6, 6, 6, 6],
      filename: FILENAME,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false, windowWidth: el.scrollWidth },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], avoid: 'tr' }
    };
  }
  function downloadPDF() {
    var el = getTarget();
    if (!el) { alert('Invoice content not found.'); return; }
    if (typeof html2pdf === 'undefined') { alert('PDF library failed to load. Please check your connection and try again.'); return; }
    try { html2pdf().set(pdfOptions()).from(el).save(); } catch(e) { console.error('PDF generation failed', e); }
  }
  function uploadToTimeline() {
    if (uploaded) return;
    if (!ORDER_ID || !INVOICE_TYPE || !BEARER) { setStatus('Timeline upload skipped (missing context)', 'error'); return; }
    var el = getTarget();
    if (!el || typeof html2pdf === 'undefined') return;
    setStatus('Uploading to Shopify timeline…');
    html2pdf().set(pdfOptions()).from(el).outputPdf('datauristring').then(function(dataUri){
      return fetch('/api/checkout/order/' + encodeURIComponent(ORDER_ID) + '/upload-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + BEARER },
        body: JSON.stringify({ pdfBase64: dataUri, invoiceType: INVOICE_TYPE, filename: FILENAME })
      });
    }).then(function(r){
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }).then(function(data){
      uploaded = true;
      setStatus(data.cached ? '✓ Already on Shopify timeline' : '✓ Attached to Shopify timeline', 'success');
    }).catch(function(err){
      console.error('Timeline upload failed', err);
      setStatus('⚠ Timeline upload failed', 'error');
    });
  }
  function init() {
    var btnPrint = document.getElementById('btn-print');
    var btnClose = document.getElementById('btn-close');
    if (btnPrint) btnPrint.onclick = function(){ window.print(); };
    if (btnClose) btnClose.onclick = function(){ window.close(); };
    var tries = 0;
    var poll = setInterval(function(){
      tries++;
      if (typeof html2pdf !== 'undefined') {
        clearInterval(poll);
        // Upload to Shopify timeline (no auto-download — user clicks button)
        setTimeout(uploadToTimeline, 300);
      } else if (tries > 50) {
        clearInterval(poll);
        setStatus('PDF library failed to load', 'error');
        console.warn('html2pdf failed to load from CDN.');
      }
    }, 100);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
</script>`;
    };

    // ── Terms & Conditions page HTML (appended to all invoices) ──
    const termsAndConditionsHtml = `
<div style="page-break-before:always"></div>
<table class="outer" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;margin-top:10px">
  <tr>
    <td colspan="3" style="text-align:center;padding:12px 10px 4px;border-bottom:1px solid #bbb">
      <div style="font-size:18px;font-weight:bold;color:#3d2b1f">SleepyCat</div>
      <div style="font-size:9px;color:#888">Sleep Solves Everything</div>
      <div style="font-size:7px;color:#888">www.sleepycat.in | +91 85880 52247 | info@sleepycat.in</div>
    </td>
  </tr>
  <tr>
    <td colspan="3" style="text-align:center;padding:8px 10px;border-bottom:2px solid #333;background:#f9f9f9">
      <div style="font-size:13px;font-weight:bold;letter-spacing:1px">Terms & Conditions — Customer Copy</div>
    </td>
  </tr>
  <tr>
    <td style="width:33%;vertical-align:top;padding:8px;border-right:1px solid #ddd;border-bottom:1px solid #ddd">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
        <strong style="font-size:8px">Adult Mattresses</strong>
        <span style="font-size:7px;color:#888;background:#f5f5f5;padding:1px 4px;border-radius:3px">100-Night Trial</span>
      </div>
      <div style="font-size:7px;font-weight:bold;margin:4px 0 2px">TRIAL & RETURNS / POLICY</div>
      <div style="font-size:6.5px;line-height:1.5;color:#333">
        • Standard sizes only — custom sizes excluded from trial<br>
        • Min. 2 weeks use required before initiating return<br>
        • Product must be clean, stain-free & undamaged to qualify<br>
        • Return request via email with photo proof required<br>
        • Refund to original payment mode; pickup in 7 working days<br>
        • Not valid on Amazon, Flipkart or quick commerce orders<br>
        • Limited to 1 mattress return per household per year
      </div>
      <div style="font-size:7px;font-weight:bold;margin:5px 0 2px">WARRANTY</div>
      <div style="font-size:6.5px;line-height:1.5;color:#333">
        • <strong>10-year warranty</strong> — full repair/replace in year 1<br>
        • Prorated credit from year 2 onward if irreparable
      </div>
    </td>
    <td style="width:33%;vertical-align:top;padding:8px;border-right:1px solid #ddd;border-bottom:1px solid #ddd">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
        <strong style="font-size:8px">Custom Mattresses</strong>
        <span style="font-size:7px;color:#888;background:#f5f5f5;padding:1px 4px;border-radius:3px">Non-returnable</span>
      </div>
      <div style="font-size:7px;font-weight:bold;margin:4px 0 2px">TRIAL & RETURNS / POLICY</div>
      <div style="font-size:6.5px;line-height:1.5;color:#333">
        • <strong>No trial period</strong> or standard returns applicable<br>
        • <strong>Cancellations</strong> not allowed once order is placed<br>
        • <strong>Replacement/refund only</strong> in case of a confirmed manufacturing defect<br>
        • <strong>Defect must be</strong> inspected, assessed & certified by SleepyCat's team before any action is taken<br>
        • Report within 72 hours of delivery with photos & unboxing video<br>
        • SleepyCat's decision on defect validity is final
      </div>
      <div style="font-size:7px;font-weight:bold;margin:5px 0 2px">WARRANTY</div>
      <div style="font-size:6.5px;line-height:1.5;color:#333">
        • <strong>10-year warranty</strong> — manufacturing defects only<br>
        • Subject to same prorated terms as standard mattresses
      </div>
    </td>
    <td style="width:33%;vertical-align:top;padding:8px;border-bottom:1px solid #ddd">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
        <strong style="font-size:8px">Pillows</strong>
        <span style="font-size:7px;color:#888;background:#f5f5f5;padding:1px 4px;border-radius:3px">7-Day Trial</span>
      </div>
      <div style="font-size:7px;font-weight:bold;margin:4px 0 2px">TRIAL & RETURNS / POLICY</div>
      <div style="font-size:6.5px;line-height:1.5;color:#333">
        • Return or exchange within 7 days of delivery<br>
        • Must be in as-good-as-new condition — no stains or damage<br>
        • Email or call within 7 days with order ID + photos<br>
        • <strong>Refund within</strong> 3–5 working days after successful pickup<br>
        • Refund to original mode; bank transfer for cash payments
      </div>
      <div style="font-size:7px;font-weight:bold;margin:5px 0 2px">WARRANTY</div>
      <div style="font-size:6.5px;line-height:1.5;color:#333">
        • <strong>1-year warranty</strong> — manufacturing defects only
      </div>
    </td>
  </tr>
  <tr>
    <td style="width:33%;vertical-align:top;padding:8px;border-right:1px solid #ddd;border-bottom:1px solid #ddd">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
        <strong style="font-size:8px">Baby Mattress & Pet Bed</strong>
        <span style="font-size:7px;color:#888;background:#f5f5f5;padding:1px 4px;border-radius:3px">Non-returnable</span>
      </div>
      <div style="font-size:7px;font-weight:bold;margin:4px 0 2px">TRIAL & RETURNS / POLICY</div>
      <div style="font-size:6.5px;line-height:1.5;color:#333">
        • No trial policy applicable on either product<br>
        • <strong>Cancellations after dispatch</strong> attract a logistics fee<br>
        • Report damage within 3 days of delivery<br>
        • <strong>Baby Mattress:</strong> 1-year warranty — repair/replace for defects<br>
        • <strong>Pet Bed:</strong> 1-year warranty — manufacturing defects only<br>
        • Warranty void if resold, gifted or bought from unauthorised seller
      </div>
    </td>
    <td style="width:33%;vertical-align:top;padding:8px;border-right:1px solid #ddd;border-bottom:1px solid #ddd">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
        <strong style="font-size:8px">Beds — Ohayo & Katachi</strong>
        <span style="font-size:7px;color:#888;background:#f5f5f5;padding:1px 4px;border-radius:3px">Non-returnable</span>
      </div>
      <div style="font-size:7px;font-weight:bold;margin:4px 0 2px">TRIAL & RETURNS / POLICY</div>
      <div style="font-size:6.5px;line-height:1.5;color:#333">
        • <strong>Replacement within 7 days</strong> only for: damage, defect, missing parts, or wrong product<br>
        • Submit 2 images — damaged part + full product view<br>
        • <strong>Ohayo:</strong> 6-month warranty (wood deformation & joinery defects)<br>
        • <strong>Katachi:</strong> 2-year warranty — manufacturing defects; excludes fabric, leatherette, wear & tear<br>
        • Natural wood variation & seasonal changes are not considered defects
      </div>
    </td>
    <td style="width:33%;vertical-align:top;padding:8px;border-bottom:1px solid #ddd">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
        <strong style="font-size:8px">Accessories & Bedding</strong>
        <span style="font-size:7px;color:#888;background:#f5f5f5;padding:1px 4px;border-radius:3px">Non-returnable</span>
      </div>
      <div style="font-size:7px;font-weight:bold;margin:4px 0 2px">TRIAL & RETURNS / POLICY</div>
      <div style="font-size:6.5px;line-height:1.5;color:#333">
        • <strong>Includes:</strong> blankets, comforters, mattress protectors, toppers, pillow cases, weighted blankets<br>
        • <strong>Replacement only</strong> if item is damaged or defective on delivery — retain original box<br>
        • Report damage within 3 days of delivery with photo proof<br>
        • <strong>1-year warranty</strong> — manufacturing defects only<br>
        • Warranty void if resold or purchased from unauthorised seller
      </div>
    </td>
  </tr>
  <tr>
    <td colspan="3" style="padding:8px;border-bottom:1px solid #ddd">
      <div style="border:1px solid #c9a050;border-radius:4px;padding:8px;background:#fffdf5">
        <div style="font-size:7.5px;font-weight:bold;margin-bottom:4px;color:#333">Delivery damage or wrong item? Report within 72 hours — required documents:</div>
        <table style="width:100%;font-size:6.5px;color:#333"><tr>
          <td style="vertical-align:top;padding:0">• Shipment label image<br>• Invoice / order confirmation image<br>• Outer packaging images</td>
          <td style="vertical-align:top;padding:0">• Image of item received (damage/wrong/missing)<br>• Packaging video (label clearly visible)<br>• Unboxing video</td>
        </tr></table>
      </div>
    </td>
  </tr>
  <tr>
    <td colspan="3" style="padding:6px 8px;border-bottom:1px solid #ddd;background:#fffbf0">
      <div style="font-size:6px;color:#333;line-height:1.5">
        <strong>Warranty does not cover (all products):</strong> Normal wear & tear · Comfort preference changes · Stains, burns or liquid damage · Damage from misuse, folding or incorrect base · Products not with original purchaser · Purchases from unauthorised sellers · Indentations under 2.54 cm · Natural material odours or minor foam softening over time. <strong>Disputes subject to Kolkata jurisdiction.</strong> | Full T&C: www.sleepycat.in
      </div>
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding:8px;border-right:1px solid #ddd;vertical-align:top">
      <div style="font-size:8px;font-weight:bold;margin-bottom:4px">Customer Acknowledgement</div>
      <div style="font-size:6.5px;color:#555;margin-bottom:12px">By signing below, I confirm I have read and agree to the above terms & conditions.</div>
      <table style="width:90%;font-size:6.5px;color:#888;margin-top:8px"><tr>
        <td style="border-top:1px solid #ccc;padding-top:3px;width:33%;text-align:center">Signature</td>
        <td style="border-top:1px solid #ccc;padding-top:3px;width:33%;text-align:center">Name</td>
        <td style="border-top:1px solid #ccc;padding-top:3px;width:33%;text-align:center">Date</td>
      </tr></table>
    </td>
    <td style="padding:8px;vertical-align:top">
      <div style="font-size:6.5px;color:#555">For detailed product-wise policy on trials, returns, warranty & care instructions, visit:</div>
      <div style="font-size:11px;font-weight:bold;color:#3d2b1f;margin:4px 0">www.sleepycat.in</div>
      <div style="font-size:6px;color:#888">Navigate to the product fine print page for full details on each category.</div>
    </td>
  </tr>
</table>`;

    // --- Common data ---
    const customerName = o.customer ? (o.customer.first_name ? `${o.customer.first_name} ${o.customer.last_name || ''}`.trim() : o.customer.name || '') : 'Walk-in Customer';
    const customerEmail = o.customer?.email || '';
    const customerPhone = o.customer?.phone || '';
    const shipping = o.shippingAddress;
    const billing = o.billingAddress;

    if (isWarehouse) {
      // ========== PROFORMA INVOICE (Warehouse Orders) ==========
      const itemsHtml = (o.lineItems || []).map((item, idx) => {
        const props = item.properties || [];
        const stripPricing = (str) => str.replace(/\s*\(?\s*\+\s*₹[\d,]+\s*included\s*\)?/gi, '').trim();
        const customSize = stripPricing((props.find(p => p.name.toLowerCase().replace(/[\s_]/g, '') === 'customsize') || {}).value || '');
        const initials = stripPricing((props.find(p => p.name.toLowerCase().replace(/[\s_]/g, '') === 'personalizationinitials') || {}).value || '');
        const cleanTitle = stripPricing(item.title);
        // If custom size exists, replace the dimensions in variant_title with custom size value
        let displayVariantTitle = item.variant_title;
        if (customSize && displayVariantTitle) {
          // Replace dimension pattern (e.g. "72 x 48 x 8" or "72 x 36") with custom size
          displayVariantTitle = displayVariantTitle.replace(/\d+\s*x\s*\d+(?:\s*x\s*\d+)?/, customSize);
        }
        const variantLabel = displayVariantTitle && displayVariantTitle !== 'Default Title' ? ` <span style="color:#666;font-size:9px">[${displayVariantTitle}]</span>` : '';
        const customSizeText = customSize ? `Custom Size: ${customSize}` : '';
        const initialsText = initials ? `Initials: ${initials}` : '';
        const deliveryProp = props.find(p => p.name.toLowerCase().includes('delivery'));
        const deliveryLabel = deliveryProp ? `${deliveryProp.name}: ${deliveryProp.value}` : '';
        const subParts = [customSizeText, initialsText, deliveryLabel].filter(Boolean);
        const subLine = subParts.length > 0 ? `<br><span style="font-size:9px;color:#555">${subParts.join(' | ')}</span>` : '';
        const hsnProp = (props.find(p => p.name.toLowerCase().replace(/[\s_]/g, '') === 'hsn' || p.name.toLowerCase().replace(/[\s_]/g, '') === 'hsncode') || {}).value || '';
        const looked = getProductTax(item.title);
        const hsnDisplay = hsnProp || looked.hsn || '-';
        return `<tr>
          <td style="border:1px solid #bbb;padding:4px 6px;font-size:11px;text-align:center">${idx + 1}</td>
          <td style="border:1px solid #bbb;padding:4px 6px;font-size:11px">
            ${cleanTitle}${variantLabel}
            ${item.sku ? ` <span style="color:#999;font-size:9px">(${item.sku})</span>` : ''}${subLine}
          </td>
          <td style="border:1px solid #bbb;padding:4px 6px;font-size:11px;text-align:center;white-space:nowrap">${hsnDisplay}</td>
          <td style="border:1px solid #bbb;padding:4px 6px;font-size:11px;text-align:right">${parseFloat(item.price).toFixed(2)}</td>
          <td style="border:1px solid #bbb;padding:4px 6px;font-size:11px;text-align:center">${item.quantity}</td>
          <td style="border:1px solid #bbb;padding:4px 6px;font-size:11px;text-align:right;font-weight:600">${(parseFloat(item.price) * item.quantity).toFixed(2)}</td>
        </tr>`;
      }).join('');

      // Build compact notes for invoice
      const attrs = o.noteAttributes || [];
      const getAttr = (name) => (attrs.find(a => a.name === name) || {}).value || '';
      const cashAmt = getAttr('Cash Amount');
      const cardAmt = getAttr('Card Amount');
      const payMethod = getAttr('Payment Method');
      const payAmt = getAttr('Payment Amount');
      const multiOrder = getAttr('Multi-Order');
      const relatedOrders = attrs.filter(a => a.name.startsWith('Related Order')).map(a => a.value);
      const gstin = getAttr('GSTIN');
      const gstCompany = getAttr('GST Company Name');
      const staffAttr = getAttr('Staff Name');
      const txnId = getAttr('Transaction ID');
      const orderLoc = getAttr('Order Location');
      const ezSplitCash = getAttr('Ezetap Split Cash Amount');
      const ezSplitCard = getAttr('Ezetap Split Card/UPI Amount');

      const noteRows = [];
      const nr = (label, val) => `<tr><td style="padding:3px 8px;font-size:10px;color:#666;border-bottom:1px solid #eee">${label}</td><td style="padding:3px 8px;font-size:10px;font-weight:600;text-align:right;border-bottom:1px solid #eee">${val}</td></tr>`;
      // Cash / Card amounts for audit
      if (cashAmt) noteRows.push(nr('Cash Amount Paid', cashAmt));
      if (cardAmt) noteRows.push(nr('Card/UPI Amount Paid', cardAmt));
      if (ezSplitCash) noteRows.push(nr('Ezetap Split Cash', ezSplitCash));
      if (ezSplitCard) noteRows.push(nr('Ezetap Split Card/UPI', ezSplitCard));
      if (!cashAmt && !cardAmt && payMethod && payAmt) noteRows.push(nr('Amount Paid', `${payMethod} - ${payAmt}`));
      // Transaction ID
      if (txnId) noteRows.push(nr('Transaction ID', txnId));
      // Order Location
      if (orderLoc) noteRows.push(nr('Order Location', orderLoc));
      // Multi-order / related invoices
      if (multiOrder) noteRows.push(nr(multiOrder, relatedOrders.join(', ')));
      // Staff
      if (staffAttr) noteRows.push(nr('Staff', staffAttr));
      const noteAttrsHtml = noteRows.join('');

      const paymentGateway = o.paymentGateways && o.paymentGateways.length > 0 ? o.paymentGateways.join(', ') : '';
      const whInvoiceDate = new Date(o.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const whPrintTime = new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });

      const html = `<!DOCTYPE html>
<html><head><title>Proforma Invoice - #${o.orderNumber}</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 10mm; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Noto Sans', Arial, Helvetica, sans-serif; color: #222; font-size: 11px; }
  .outer { border: 2px solid #333; width: 100%; }
  .outer td { vertical-align: top; }
  .sh { background: #f5f5f5; font-weight: bold; font-size: 10px; padding: 4px 8px; border-bottom: 1px solid #bbb; text-transform: uppercase; }
</style></head><body>
<table class="outer" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%">
  <tr>
    <td colspan="4" style="text-align:center;padding:8px 8px 4px;border-bottom:1px solid #bbb">
      <svg width="97" height="32" viewBox="0 0 995 329" fill="none" xmlns="http://www.w3.org/2000/svg" style="height:32px;width:97px;display:inline-block">
        <path d="M483.625 62.6664C487.601 48.751 489.589 32.8477 485.613 19.9262C483.625 12.9685 479.649 7.00479 473.685 4.02292C465.733 -0.946869 455.793 -0.946869 447.841 2.035C437.901 6.01084 428.955 13.9625 421.003 20.9202C415.039 26.8839 409.075 33.8416 404.105 40.7994C405.099 33.8416 407.087 27.8779 407.087 20.9202C407.087 16.9444 408.081 12.9685 407.087 8.99271C406.093 5.01688 404.105 4.02292 400.129 3.02896C395.159 2.035 389.195 2.035 384.225 3.02896C378.261 2.035 374.285 4.02292 372.297 8.99271C370.309 13.9625 370.309 18.9323 370.309 23.9021C370.309 27.8779 369.315 31.8537 369.315 36.8235C367.327 58.6906 368.321 80.5577 367.327 102.425V108.388C367.327 110.376 367.327 111.37 366.333 113.358C363.351 118.328 360.369 123.298 356.393 127.274C349.435 137.213 340.489 146.159 330.549 152.123C324.585 156.098 316.633 160.074 308.681 160.074C299.735 159.08 295.759 151.129 293.771 143.177C292.777 140.195 292.777 137.213 292.777 135.225C317.627 122.304 335.519 96.461 340.489 68.6302C343.471 52.7268 344.465 33.8416 333.531 19.9262C325.579 9.98666 313.651 6.01084 301.723 7.99875C295.759 8.99271 289.795 11.9746 284.825 14.9565C279.855 18.9323 275.879 22.9081 272.897 28.8719C263.951 43.7812 261.963 61.6725 260.969 79.5637C259.975 96.461 259.975 113.358 260.969 130.256C260.969 134.231 261.963 138.207 262.957 142.183C262.957 144.171 263.951 146.159 263.951 147.153C263.951 148.147 263.951 149.141 264.945 150.135C264.945 151.129 264.945 152.123 263.951 153.117C260.969 161.068 256.993 168.026 251.029 174.984C244.071 183.929 232.143 195.857 220.215 192.875C209.281 189.893 201.329 171.008 199.341 159.08C224.191 140.195 237.113 106.401 237.113 75.5879C237.113 64.6543 235.125 52.7268 230.155 42.7873C223.197 27.8779 207.293 20.9202 189.401 27.8779C175.485 33.8417 170.515 46.7631 166.539 60.6785C162.563 74.5939 160.575 99.4429 161.569 113.358C162.563 133.237 165.545 156.098 171.509 174.984C176.479 190.887 181.449 202.814 195.365 212.754C200.335 216.73 206.299 219.712 213.257 220.706C220.215 221.7 227.173 220.706 234.131 219.712C249.041 215.736 261.963 199.833 270.909 187.905C272.897 184.923 274.885 181.941 276.873 177.966C279.855 182.935 284.825 186.911 289.795 189.893C296.753 193.869 303.711 194.863 311.663 193.869C329.555 191.881 344.465 182.935 356.393 168.026C359.375 164.05 362.357 160.074 365.339 155.104C366.333 154.111 368.321 151.129 368.321 150.135C371.303 186.911 372.297 200.827 385.219 248.537C387.207 255.494 400.129 258.476 405.099 253.506C412.057 246.549 408.081 237.603 407.087 229.651C402.117 195.857 399.135 162.062 399.135 128.268C419.015 134.231 439.889 122.304 453.805 109.382C468.715 96.461 478.655 80.5577 483.625 62.6664ZM194.371 131.25C192.383 118.328 190.395 97.4549 192.383 77.5758C194.371 59.6846 199.341 48.751 202.323 47.7571C206.299 47.7571 207.293 53.7208 207.293 56.7027C209.281 62.6664 210.275 68.6302 210.275 74.5939C210.275 88.5093 207.293 107.395 201.329 120.316C199.341 124.292 197.353 127.274 194.371 131.25ZM293.771 59.6846C295.759 50.7389 300.729 31.8537 309.675 31.8537C316.633 31.8537 315.639 47.7571 315.639 50.7389C314.645 63.6604 312.657 76.5818 306.693 88.5093C303.711 95.467 298.741 101.431 291.783 105.407C290.789 91.4912 290.789 73.6 293.771 59.6846ZM449.829 62.6664C445.853 70.6181 439.889 80.5577 430.943 88.5093C414.045 102.425 406.093 98.4489 405.099 97.4549C404.105 96.461 405.099 93.4791 405.099 92.4852C408.081 76.5818 418.021 61.6725 428.955 48.751C431.937 44.7752 446.847 27.8779 454.799 32.8477C460.763 35.8296 454.799 52.7268 449.829 62.6664ZM58.1929 165.044C54.2169 161.068 50.2409 156.098 47.2589 152.123C39.3069 143.177 32.3489 132.243 30.3609 120.316C28.3729 109.382 32.3489 99.4429 41.2949 92.4852C48.2529 86.5214 55.2109 80.5577 50.2409 73.6C46.2649 68.6302 39.3069 67.6362 26.3849 73.6C15.4509 78.5697 -3.43511 93.4791 0.540893 130.256C3.52289 154.111 20.4209 174.984 36.3249 190.887C58.1929 213.748 67.1389 236.609 66.1449 255.494C65.1509 266.428 60.1809 275.373 53.2229 284.319C50.2409 288.295 46.2649 292.271 42.2889 296.246C39.3069 299.228 35.3309 302.21 32.3489 306.186C27.3789 313.144 29.3669 323.083 37.3189 327.059C44.2769 330.041 52.2289 329.047 59.1869 325.071C76.0849 316.126 87.0189 302.21 93.9769 283.325C100.935 264.44 100.935 243.567 94.9709 224.682C89.0069 201.821 74.0969 182.935 58.1929 165.044Z" fill="#c88346"></path>
        <path d="M235.125 250.524C234.131 249.531 232.143 249.531 231.149 249.531C218.227 247.543 207.293 249.531 195.365 251.518C192.383 252.512 161.569 259.47 160.575 258.476C151.629 214.742 143.677 173.99 140.695 158.086C137.713 144.171 135.725 130.256 132.743 116.34C130.755 103.419 127.773 90.4973 124.791 77.5758C122.803 69.6241 119.821 61.6725 114.851 55.7087C112.863 52.7269 108.887 50.7389 105.905 51.7329C102.923 51.7329 96.9589 55.7087 96.9589 58.6906C96.9589 64.6544 97.9529 70.6181 98.9469 76.5819L101.929 101.431C104.911 121.31 106.899 141.189 109.881 161.068L118.827 223.688C120.815 239.591 123.797 255.494 126.779 271.398C127.773 277.361 128.767 284.319 131.749 290.283C132.743 293.265 134.731 296.247 138.707 297.24C142.683 298.234 169.521 291.277 179.461 289.289C189.401 287.301 229.161 282.331 236.119 278.355C242.083 275.373 243.077 269.41 243.077 262.452C241.089 255.494 239.101 252.512 235.125 250.524ZM607.875 15.9504C601.911 8.99272 588.989 9.98668 583.025 24.896C569.109 60.6785 554.199 83.5396 538.295 94.4731C537.301 95.4671 534.319 98.4489 533.325 94.4731V88.5093C533.325 84.5335 534.319 81.5516 534.319 77.5758C536.307 65.6483 537.301 54.7148 537.301 43.7812C537.301 33.8417 538.295 23.9021 536.307 13.9625C535.313 7.99876 531.337 0.0471012 524.379 3.02897C516.427 6.01085 514.439 17.9383 512.451 25.89C509.469 35.8296 508.475 45.7692 506.487 55.7087C505.493 65.6483 504.499 76.5819 504.499 86.5214C504.499 96.461 503.505 106.401 506.487 115.346C507.481 118.328 509.469 121.31 511.457 123.298C513.445 125.286 516.427 127.274 519.409 128.268C525.373 130.256 533.325 130.256 539.289 128.268C554.199 124.292 565.133 110.376 574.079 99.4429L575.073 98.4489C556.187 178.96 530.343 226.669 505.493 221.7C497.541 219.712 492.571 210.766 491.577 203.808C489.589 194.863 488.595 185.917 485.613 176.972C483.625 171.008 477.661 160.074 466.727 165.044C456.787 169.02 457.781 182.935 458.775 195.857C459.769 211.76 468.715 251.518 503.505 254.5C539.289 257.482 568.115 235.615 588.989 158.086C597.935 126.28 610.857 63.6604 611.851 29.8658C611.851 26.884 611.851 19.9263 607.875 15.9504ZM802.699 41.7933C789.777 40.7994 781.825 52.7269 776.855 63.6604C748.029 132.243 714.233 195.857 692.365 265.434C690.377 272.392 693.359 281.337 700.317 283.325C712.245 288.295 719.203 275.373 722.185 267.422C733.119 238.597 744.053 210.766 755.981 183.929C765.921 186.911 774.867 188.899 784.807 191.881C780.831 223.688 776.855 248.537 773.873 289.289C772.879 299.228 782.819 310.162 793.753 304.198C800.711 300.222 801.705 289.289 802.699 281.337C812.639 208.778 820.591 138.207 822.579 64.6544C823.573 53.7208 815.621 42.7873 802.699 41.7933ZM795.741 88.5093C795.741 87.5154 795.741 87.5154 795.741 88.5093H796.735V89.5033C792.759 114.352 789.777 139.201 786.795 164.05L766.915 158.086C776.855 135.225 785.801 112.364 795.741 88.5093ZM985.595 106.401C961.739 95.4671 942.853 87.5154 918.003 78.5698C906.075 74.5939 894.147 70.6181 882.219 67.6362C873.273 65.6483 860.351 61.6725 854.387 69.6241C846.435 78.5698 856.375 90.4973 865.321 94.4731C876.255 98.4489 886.195 101.431 895.141 104.413C888.183 122.304 883.213 140.195 878.243 158.086C862.339 211.76 842.459 251.518 828.543 305.192C825.561 319.108 834.507 325.071 840.471 324.077C850.411 322.089 856.375 306.186 863.333 287.301C885.201 226.669 909.057 174.984 923.967 132.243C925.955 126.28 927.943 121.31 929.931 115.346C941.859 119.322 954.781 123.298 968.697 128.268C993.547 138.207 1003.49 114.352 985.595 106.401ZM681.431 225.676C677.455 223.688 672.485 222.694 667.515 224.682C663.539 225.676 659.563 227.663 655.587 227.663C650.617 227.663 645.647 224.682 644.653 218.718C642.665 212.754 643.659 206.79 643.659 200.827C644.653 174.984 649.623 149.141 656.581 124.292C659.563 112.364 663.539 100.437 667.515 89.5033C669.503 83.5396 672.485 77.5758 675.467 71.6121C678.449 66.6423 681.431 59.6846 687.395 56.7027C693.359 53.7208 701.311 56.7027 705.287 64.6544C708.269 70.6181 708.269 76.5819 713.239 81.5516C718.209 87.5154 730.137 89.5033 737.095 78.5698C743.059 68.6302 737.095 54.7148 731.131 45.7692C726.161 37.8175 719.203 29.8658 709.263 25.89C699.323 21.9142 686.401 23.9021 676.461 27.8779C666.521 31.8538 657.575 38.8115 651.611 48.751C643.659 59.6846 638.689 70.6181 633.719 82.5456C628.749 94.4731 624.773 106.401 620.797 118.328C616.821 130.256 613.839 143.177 611.851 156.098C609.863 170.014 608.869 183.929 609.863 197.845C610.857 210.766 612.845 222.694 618.809 234.621C623.779 244.561 632.725 251.518 643.659 255.494C652.605 258.476 664.533 259.47 673.479 255.494C680.437 252.512 690.377 244.561 689.383 235.615C688.389 230.645 685.407 227.663 681.431 225.676Z" fill="#c88346"></path>
      </svg>
    </td>
  </tr>
  <tr>
    <td colspan="4" style="text-align:center;padding:5px 8px;border-bottom:2px solid #333;background:#f9f9f9">
      <div style="font-size:14px;font-weight:bold;letter-spacing:2px">PROFORMA INVOICE</div>
      <div style="font-size:9px;color:#666;margin-top:2px">Price inclusive of all taxes</div>
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding:6px 8px;border-bottom:1px solid #bbb;border-right:1px solid #bbb;width:50%;font-size:11px;line-height:1.5">
      <strong>Sleep Management Pvt. Ltd.</strong><br>
      ${(o.originStore && o.originStore.name) ? o.originStore.name + '<br>' : (o.storeLocation && o.storeLocation.name) ? o.storeLocation.name + '<br>' : ''}
      ${(o.originStore && o.originStore.address) ? o.originStore.address.replace(/\n/g, '<br>') + '<br>' : (o.storeLocation && o.storeLocation.address) ? o.storeLocation.address.replace(/\n/g, '<br>') + '<br>' : ''}
      ${(o.originStore && o.originStore.phone) ? 'Phone : ' + o.originStore.phone : (o.storeLocation && o.storeLocation.phone) ? 'Phone : ' + o.storeLocation.phone : ''}
    </td>
    <td colspan="2" style="padding:6px 8px;border-bottom:1px solid #bbb;width:50%;font-size:11px">
      <table style="width:100%;font-size:11px;border-collapse:collapse">
        <tr><td style="padding:2px 0;font-weight:bold">Invoice No:</td><td style="text-align:right">${getAttr('Invoice Number') || o.name || '#' + o.orderNumber}</td></tr>
        ${o.name ? `<tr><td style="padding:2px 0;font-weight:bold">Order No:</td><td style="text-align:right">${o.name}</td></tr>` : ''}
        <tr><td style="padding:2px 0;font-weight:bold">Date:</td><td style="text-align:right">${whInvoiceDate}</td></tr>
        <tr><td style="padding:2px 0;font-weight:bold">Print:</td><td style="text-align:right">${whPrintTime}</td></tr>
        <tr><td style="padding:2px 0;font-weight:bold">Status:</td><td style="text-align:right">${o.cancelledAt ? 'Cancelled' : (o.financialStatus || 'Pending')}</td></tr>
        ${(payMethod || paymentGateway) ? `<tr><td style="padding:2px 0;font-weight:bold">Payment:</td><td style="text-align:right">${payMethod || paymentGateway}</td></tr>` : ''}
      </table>
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding:0;border-bottom:1px solid #bbb;border-right:1px solid #bbb;width:50%">
      <div class="sh">Bill to / Buyer</div>
      <div style="padding:5px 8px;font-size:11px;line-height:1.5">
        ${gstCompany ? `<strong>${gstCompany}</strong><br>` : ''}
        <strong>${customerName}</strong>
        ${customerPhone ? `<br>Ph: ${customerPhone}` : ''}
        ${customerEmail ? `<br>${customerEmail}` : ''}
        ${(() => { const b = (billing && billing.address1) ? billing : shipping; return b ? `${b.address1 ? '<br>' + b.address1 + (b.address2 ? ', ' + b.address2 : '') : ''}${b.city ? '<br>' + b.city + (b.province ? ', ' + b.province : '') + ' ' + (b.zip || '') : ''}${b.country ? '<br>' + b.country : ''}` : ''; })()}
        ${gstin ? `<br><span style="font-size:10px;color:#444">GSTIN: ${gstin}</span>` : ''}
      </div>
    </td>
    <td colspan="2" style="padding:0;border-bottom:1px solid #bbb;width:50%">
      <div class="sh">Ship To</div>
      <div style="padding:5px 8px;font-size:11px;line-height:1.5">
        ${gstCompany ? `<strong>${gstCompany}</strong><br>` : ''}
        ${shipping ? `${shipping.name || ''}<br>${shipping.address1 || ''}${shipping.address2 ? ', ' + shipping.address2 : ''}<br>${shipping.city || ''}, ${shipping.province || ''} ${shipping.zip || ''}<br>${shipping.country || ''}${shipping.phone ? '<br>Ph: ' + shipping.phone : ''}` : '-'}
        ${gstin ? `<br><span style="font-size:10px;color:#444">GSTIN: ${gstin}</span>` : ''}
      </div>
    </td>
  </tr>
  <tr>
    <td colspan="4" style="padding:0;border-bottom:1px solid #bbb">
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:#f0f0f0">
            <th style="border:1px solid #bbb;padding:4px 6px;font-size:10px;text-align:center;width:25px">#</th>
            <th style="border:1px solid #bbb;padding:4px 6px;font-size:10px;text-align:left">Product</th>
            <th style="border:1px solid #bbb;padding:4px 6px;font-size:10px;text-align:center;width:80px;white-space:nowrap">HSN</th>
            <th style="border:1px solid #bbb;padding:4px 6px;font-size:10px;text-align:right;width:70px">Rate (\u20b9)</th>
            <th style="border:1px solid #bbb;padding:4px 6px;font-size:10px;text-align:center;width:35px">Qty</th>
            <th style="border:1px solid #bbb;padding:4px 6px;font-size:10px;text-align:right;width:80px">Total (\u20b9)</th>
          </tr>
        </thead>
        <tbody>${itemsHtml}</tbody>
      </table>
    </td>
  </tr>
  <tr>
    <td colspan="4" style="padding:5px 8px;border-bottom:1px solid #bbb;font-size:11px;font-weight:bold">
      Total Items: ${(o.lineItems || []).length}, Qty: ${(o.lineItems || []).reduce((s, li) => s + li.quantity, 0)}
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding:0;border-right:1px solid #bbb;vertical-align:top;width:50%">
      <div class="sh">Payment</div>
      <table style="width:100%;border-collapse:collapse;font-size:11px;table-layout:fixed">
        <tr><td style="padding:4px 8px;border-bottom:1px solid #eee">${payMethod || paymentGateway || '-'}</td><td style="padding:4px 8px;text-align:right;border-bottom:1px solid #eee;width:90px">\u20b9${parseFloat(o.total || 0).toFixed(2)}</td></tr>
      </table>
      ${noteAttrsHtml ? `<div class="sh" style="margin-top:0">Notes</div><table style="width:100%;border-collapse:collapse;table-layout:fixed">${noteAttrsHtml}</table>` : ''}
    </td>
    <td colspan="2" style="padding:0;vertical-align:top">
      <div class="sh">Summary</div>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <tr><td style="padding:4px 8px;border-bottom:1px solid #eee">Subtotal</td><td style="padding:4px 8px;text-align:right;border-bottom:1px solid #eee">\u20b9${(parseFloat(o.total || 0) + parseFloat(o.totalDiscounts || 0)).toFixed(2)}</td></tr>
        ${o.totalDiscounts && parseFloat(o.totalDiscounts) > 0 ? `<tr><td style="padding:4px 8px;border-bottom:1px solid #eee;color:#dc2626">Discount</td><td style="padding:4px 8px;text-align:right;border-bottom:1px solid #eee;color:#dc2626">-\u20b9${parseFloat(o.totalDiscounts).toFixed(2)}</td></tr>` : ''}
        <tr><td style="padding:4px 8px;border-bottom:1px solid #eee">Order Value</td><td style="padding:4px 8px;text-align:right;border-bottom:1px solid #eee">\u20b9${parseFloat(o.total || 0).toFixed(2)}</td></tr>
        <tr style="font-weight:bold;font-size:12px;background:#f9f9f9"><td style="padding:6px 8px;border-top:2px solid #333">Total</td><td style="padding:6px 8px;text-align:right;border-top:2px solid #333">\u20b9${parseFloat(o.total || 0).toFixed(2)}</td></tr>
      </table>
    </td>
  </tr>
  <tr>
    <td colspan="4" style="padding:8px;text-align:center;border-top:1px solid #bbb;font-size:9px;color:#888">
      This is a computer-generated proforma invoice and does not require a signature. | info@sleepycat.in
    </td>
  </tr>
</table>
${invoiceActionsBar(`Proforma-Invoice-${(o.name || o.orderNumber || 'order').toString().replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`, o.id, 'proforma')}
${termsAndConditionsHtml}
</body></html>`;

      const printWindow = window.open('', '_blank', 'width=900,height=1100');
      printWindow.document.write(html);
      printWindow.document.close();
      return;
    }

    // ========== PROFORMA INVOICE (Store Orders) ==========

    // --- Number to words conversion ---
    const numberToWords = (num) => {
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      if (num === 0) return 'Zero';
      const convert = (n) => {
        if (n < 20) return ones[n];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
        if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
        if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
        if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
        return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
      };
      const rupees = Math.floor(num);
      const paise = Math.round((num - rupees) * 100);
      let words = 'Rupees ' + convert(rupees);
      if (paise > 0) words += ' and ' + convert(paise) + ' Paise';
      return words + ' Only';
    };

    // --- Extract location name from tags ---
    const knownTags = ['pos', 'in-store', 'warehouse', 'store', 'split-payment', 'cash', 'card', 'upi', 'net-banking', 'snapmint', 'district-credit', 'gst-billing'];
    const locationName = (o.tags || '').split(',').map(t => t.trim()).filter(t => t && !knownTags.includes(t.toLowerCase()))[0] || '';

    // --- Company info ---
    const companyName = 'SLEEP MANAGEMENT PRIVATE LIMITED';

    // --- Store location from API (resolved from order tags) ---
    const storeLoc = o.storeLocation || {};
    const storeAddress = storeLoc.address || '';
    const storePhone = storeLoc.phone || '';
    const storeGST = storeLoc.gstin || '';

    // --- Seller GST from customer GSTIN note attribute or default ---
    const customerGSTIN = (o.noteAttributes || []).find(a => a.name === 'GSTIN' || a.name === 'customergstin')?.value || '';
    const gstCompanyName = (o.noteAttributes || []).find(a => a.name === 'GST Company Name')?.value || '';

    // --- Self-invoice number (from note_attributes or DB fallback) ---
    const selfInvoiceNumber = (o.noteAttributes || []).find(a => a.name === 'Invoice Number')?.value || '';

    // --- Billing user / staff ---
    const billingUser = (o.noteAttributes || []).find(a => a.name === 'Billing User' || a.name === 'Staff')?.value || '';

    // --- Payment info from note attributes ---
    const paymentMethodAttr = (o.noteAttributes || []).find(a => a.name === 'Payment Method')?.value || '';
    const cashAmountAttr = (o.noteAttributes || []).find(a => a.name === 'Cash Amount')?.value || '';
    const cardAmountAttr = (o.noteAttributes || []).find(a => a.name === 'Card Amount')?.value || '';
    const upiAmountAttr = (o.noteAttributes || []).find(a => a.name === 'UPI Amount')?.value || '';
    const transactionIdAttr = (o.noteAttributes || []).find(a => a.name === 'Transaction ID')?.value || '';

    // Build payment rows
    const paymentRows = [];
    if (paymentMethodAttr.toLowerCase().includes('split')) {
      if (cashAmountAttr) paymentRows.push({ method: 'Cash', amount: cashAmountAttr });
      if (upiAmountAttr) paymentRows.push({ method: 'UPI', amount: upiAmountAttr });
      if (cardAmountAttr) paymentRows.push({ method: 'Card', amount: cardAmountAttr });
    } else {
      // Use the order total (matches the Shopify invoice PDF, which always prints ₹total), NOT the
      // stored "Payment Amount" note attribute — older orders baked in a pre-cascade value that
      // overstated the discount and so didn't match the invoice total.
      paymentRows.push({ method: paymentMethodAttr || (o.paymentGateways || []).join(', ') || '-', amount: `₹${parseFloat(o.total || 0).toFixed(2)}` });
    }
    if (transactionIdAttr) paymentRows.push({ method: 'Transaction ID', amount: transactionIdAttr });

    // --- Date/time formatting ---
    const orderDate = new Date(o.createdAt);
    const invoiceDate = orderDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const printTime = new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });

    // --- Totals ---
    // eslint-disable-next-line no-unused-vars
    const subtotal = parseFloat(o.subtotal || 0);
    const totalDiscounts = parseFloat(o.totalDiscounts || 0);
    const total = parseFloat(o.total || 0);

    // --- GST calculation (inclusive, per item, discount applied before tax) ---
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;
    let totalTaxableAmt = 0;
    // eslint-disable-next-line no-unused-vars
    let totalItemDiscount = 0;

    // Inter-state (IGST) is flagged at checkout via the 'GST Type' note attribute.
    const orderInterState = ((o.noteAttributes || []).find(a => a.name === 'GST Type')?.value || '').toUpperCase() === 'IGST';

    // Calculate gross total (for the legacy proportional fallback only)
    const lineItems = o.lineItems || [];
    const grossTotal = lineItems.reduce((s, li) => s + parseFloat(li.price) * li.quantity, 0);
    const totalQty = lineItems.reduce((s, li) => s + li.quantity, 0);

    // Frozen per-line GST from Shopify tax_lines (set at checkout on the product-then-order
    // cascaded net) — the authoritative per-line tax. Using it (instead of re-splitting the
    // discount evenly + recomputing from a rate) makes the reprint match the server invoice
    // and the filed GST.
    const frozenLineTax = (li) => {
      const tl = li.tax_lines || [];
      if (!tl.length) return null;
      let cgst = 0, sgst = 0, igst = 0, cgstRate = 0, sgstRate = 0, igstRate = 0;
      for (const t of tl) {
        const amt = parseFloat(t.price) || 0;
        const ratePct = (parseFloat(t.rate) || 0) * 100;
        const title = (t.title || '').toUpperCase();
        if (title.includes('IGST')) { igst += amt; igstRate = ratePct || igstRate; }
        else if (title.includes('CGST')) { cgst += amt; cgstRate = ratePct || cgstRate; }
        else if (title.includes('SGST')) { sgst += amt; sgstRate = ratePct || sgstRate; }
      }
      return { cgst, sgst, igst, cgstRate, sgstRate, igstRate, total: cgst + sgst + igst };
    };

    const itemsHtml = lineItems.map((item, idx) => {
      const price = parseFloat(item.price);
      const qty = item.quantity;
      const lineTotal = price * qty;

      const frozen = frozenLineTax(item);
      const frozenRate = frozen ? (frozen.igstRate || (frozen.cgstRate + frozen.sgstRate)) : 0;

      // Per-line discount, in priority order (mirrors the server invoice + the cascade):
      //  1. `_line_discount` frozen at checkout — exact product-then-order per-line allocation,
      //  2. back-calc from the frozen tax (net = taxable + tax) — for orders before it was persisted,
      //  3. proportional split (legacy, only when there is no tax info at all).
      // Tiers 2 & 3 only apply when the order actually carries a discount — on a no-discount order
      // the back-calc loses a paisa of rounding and would show a phantom "-0.03" discount.
      const lineDiscProp = (item.properties || []).find(p => (p.name || '').toLowerCase() === '_line_discount');
      let itemDiscount = 0;
      if (lineDiscProp) itemDiscount = parseFloat(lineDiscProp.value) || 0;
      else if (totalDiscounts > 0 && frozen && frozenRate > 0) itemDiscount = lineTotal - ((frozen.total / (frozenRate / 100)) + frozen.total);
      else if (totalDiscounts > 0) itemDiscount = grossTotal > 0 ? (lineTotal / grossTotal) * totalDiscounts : 0;
      if (itemDiscount < 0) itemDiscount = 0;
      const discountedTotal = lineTotal - itemDiscount;
      totalItemDiscount += itemDiscount;

      // Tax: prefer the frozen tax_lines; else recompute from a rate (legacy pre-GST-module orders).
      let taxableAmt, cgst, sgst, igst, gstRate;
      if (frozen) {
        cgst = frozen.cgst; sgst = frozen.sgst; igst = frozen.igst;
        gstRate = frozen.igstRate || (frozen.cgstRate + frozen.sgstRate);
        taxableAmt = discountedTotal - frozen.total;
      } else {
        const gstProp = (item.properties || []).find(p => p.name.toLowerCase() === 'gst_rate' || p.name.toLowerCase() === 'gst rate');
        gstRate = gstProp ? parseFloat(gstProp.value) : getProductTax(item.title).gst;
        taxableAmt = discountedTotal / (1 + gstRate / 100);
        const lineTax = discountedTotal - taxableAmt;
        igst = orderInterState ? lineTax : 0;
        cgst = orderInterState ? 0 : lineTax / 2;
        sgst = orderInterState ? 0 : lineTax / 2;
      }
      const halfRate = gstRate / 2;
      totalCGST += cgst;
      totalSGST += sgst;
      totalIGST += igst;
      totalTaxableAmt += taxableAmt;
      const variantLabel = item.variant_title && item.variant_title !== 'Default Title' ? ` (${item.variant_title})` : '';
      const propsText = (item.properties || []).filter(p => !(p.name || '').startsWith('_') && p.name.toLowerCase() !== 'gst_rate' && p.name.toLowerCase() !== 'gst rate' && p.name.toLowerCase().replace(/[\s_]/g, '') !== 'hsn' && p.name.toLowerCase().replace(/[\s_]/g, '') !== 'hsncode').map(p => `${p.name}: ${p.value}`).join(', ');
      const hsnProp = (item.properties || []).find(p => p.name.toLowerCase().replace(/[\s_]/g, '') === 'hsn' || p.name.toLowerCase().replace(/[\s_]/g, '') === 'hsncode');
      const hsnCode = hsnProp ? hsnProp.value : (getProductTax(item.title).hsn || '');
      const taxCols = orderInterState
        ? `<td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:center">${gstRate}%</td>
           <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:right">${igst.toFixed(2)}</td>`
        : `<td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:center">${halfRate}%</td>
           <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:right">${cgst.toFixed(2)}</td>
           <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:center">${halfRate}%</td>
           <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:right">${sgst.toFixed(2)}</td>`;
      return `<tr>
        <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px">${idx + 1}</td>
        <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px">
          ${item.title}${variantLabel}
          ${item.sku ? `<br><span style="color:#888;font-size:10px">SKU: ${item.sku}</span>` : ''}
          ${propsText ? `<br><span style="color:#888;font-size:10px">${propsText}</span>` : ''}
        </td>
        <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:center;white-space:nowrap">${hsnCode || '-'}</td>
        <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:right">${price.toFixed(2)}</td>
        <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:center">${qty}</td>
        ${itemDiscount > 0 ? `<td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:right;color:#dc2626">-${itemDiscount.toFixed(2)}</td>` : `<td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:center">-</td>`}
        <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:right">${taxableAmt.toFixed(2)}</td>
        ${taxCols}
        <td style="border:1px solid #bbb;padding:6px 8px;font-size:11px;text-align:right;font-weight:600">${discountedTotal.toFixed(2)}</td>
      </tr>`;
    }).join('');

    // eslint-disable-next-line no-unused-vars
    const totalGST = totalCGST + totalSGST + totalIGST;
    const computedTotal = totalTaxableAmt + totalCGST + totalSGST + totalIGST;
    const rounding = Math.round((total - computedTotal) * 100) / 100;

    // --- Determine if billing & shipping differ ---
    const billAddr = billing || shipping;
    const shipAddr = shipping;
    const hasBothAddresses = billing && shipping && (billing.address1 !== shipping.address1 || billing.city !== shipping.city);

    // --- Left column: "Sold By / Seller" OR "Ship To" if addresses differ ---
    const sellerBlockLabel = hasBothAddresses ? 'Ship To' : 'Sold By / Seller';
    const sellerBlock = hasBothAddresses
      ? `<div style="font-size:11px;line-height:1.6">
      ${gstCompanyName ? '<strong>' + gstCompanyName + '</strong><br>' : ''}
      <strong>${shipAddr.name || customerName}</strong><br>
      ${shipAddr.address1 || ''}${shipAddr.address2 ? ', ' + shipAddr.address2 : ''}<br>
      ${shipAddr.city || ''}${shipAddr.province ? ', ' + shipAddr.province : ''} ${shipAddr.zip || ''}<br>
      ${shipAddr.country || ''}<br>
      ${shipAddr.phone ? 'Phone: ' + shipAddr.phone + '<br>' : ''}
      ${customerGSTIN ? 'GSTIN: ' + customerGSTIN + '<br>' : ''}
    </div>`
      : `<div style="font-size:11px;line-height:1.6">
      <strong>${companyName}</strong><br>
      ${storeLoc.name || locationName ? (storeLoc.name || locationName) + '<br>' : ''}
      ${storeAddress ? `${storeAddress.replace(/\n/g, '<br>')}<br>` : ''}
      ${billingUser ? `Billing User : ${billingUser}<br>` : ''}
      ${storePhone ? `Phone : ${storePhone}<br>` : ''}
      ${storeGST ? `GSTIN : ${storeGST}` : ''}
    </div>`;

    // --- Invoice To block (billing address only, no ship-to sub-section) ---
    const invoiceToBlock = `<div style="font-size:11px;line-height:1.6">
      ${gstCompanyName ? '<strong>' + gstCompanyName + '</strong><br>' : ''}
      <strong>${customerName}</strong><br>
      ${billAddr ? `${billAddr.address1 || ''}${billAddr.address2 ? ', ' + billAddr.address2 : ''}<br>
      ${billAddr.city || ''}${billAddr.province ? ', ' + billAddr.province : ''} ${billAddr.zip || ''}<br>
      ${billAddr.country || ''}<br>` : ''}
      Phone: ${customerPhone || '-'}<br>
      ${customerEmail ? `Email: ${customerEmail}<br>` : ''}
      ${customerGSTIN ? `GSTIN: ${customerGSTIN}` : ''}
    </div>`;

    const html = `<!DOCTYPE html>
<html><head><title>Tax Invoice - ${selfInvoiceNumber || '#' + o.orderNumber}</title>
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 12mm; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Noto Sans', Arial, Helvetica, sans-serif; color: #222; font-size: 12px; }
  .outer { border: 2px solid #333; width: 100%; }
  .outer td { vertical-align: top; }
  .section-header { background: #f5f5f5; font-weight: bold; font-size: 11px; padding: 6px 10px; border-bottom: 1px solid #bbb; text-transform: uppercase; }
</style></head><body>

<table class="outer" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%">

  <!-- Row 1: Brand Header -->
  <tr>
    <td colspan="6" style="text-align:center;padding:12px 10px 6px;border-bottom:1px solid #bbb;">
      <svg width="121" height="40" viewBox="0 0 995 329" fill="none" xmlns="http://www.w3.org/2000/svg" style="height:40px;width:121px;display:inline-block">
        <path d="M483.625 62.6664C487.601 48.751 489.589 32.8477 485.613 19.9262C483.625 12.9685 479.649 7.00479 473.685 4.02292C465.733 -0.946869 455.793 -0.946869 447.841 2.035C437.901 6.01084 428.955 13.9625 421.003 20.9202C415.039 26.8839 409.075 33.8416 404.105 40.7994C405.099 33.8416 407.087 27.8779 407.087 20.9202C407.087 16.9444 408.081 12.9685 407.087 8.99271C406.093 5.01688 404.105 4.02292 400.129 3.02896C395.159 2.035 389.195 2.035 384.225 3.02896C378.261 2.035 374.285 4.02292 372.297 8.99271C370.309 13.9625 370.309 18.9323 370.309 23.9021C370.309 27.8779 369.315 31.8537 369.315 36.8235C367.327 58.6906 368.321 80.5577 367.327 102.425V108.388C367.327 110.376 367.327 111.37 366.333 113.358C363.351 118.328 360.369 123.298 356.393 127.274C349.435 137.213 340.489 146.159 330.549 152.123C324.585 156.098 316.633 160.074 308.681 160.074C299.735 159.08 295.759 151.129 293.771 143.177C292.777 140.195 292.777 137.213 292.777 135.225C317.627 122.304 335.519 96.461 340.489 68.6302C343.471 52.7268 344.465 33.8416 333.531 19.9262C325.579 9.98666 313.651 6.01084 301.723 7.99875C295.759 8.99271 289.795 11.9746 284.825 14.9565C279.855 18.9323 275.879 22.9081 272.897 28.8719C263.951 43.7812 261.963 61.6725 260.969 79.5637C259.975 96.461 259.975 113.358 260.969 130.256C260.969 134.231 261.963 138.207 262.957 142.183C262.957 144.171 263.951 146.159 263.951 147.153C263.951 148.147 263.951 149.141 264.945 150.135C264.945 151.129 264.945 152.123 263.951 153.117C260.969 161.068 256.993 168.026 251.029 174.984C244.071 183.929 232.143 195.857 220.215 192.875C209.281 189.893 201.329 171.008 199.341 159.08C224.191 140.195 237.113 106.401 237.113 75.5879C237.113 64.6543 235.125 52.7268 230.155 42.7873C223.197 27.8779 207.293 20.9202 189.401 27.8779C175.485 33.8417 170.515 46.7631 166.539 60.6785C162.563 74.5939 160.575 99.4429 161.569 113.358C162.563 133.237 165.545 156.098 171.509 174.984C176.479 190.887 181.449 202.814 195.365 212.754C200.335 216.73 206.299 219.712 213.257 220.706C220.215 221.7 227.173 220.706 234.131 219.712C249.041 215.736 261.963 199.833 270.909 187.905C272.897 184.923 274.885 181.941 276.873 177.966C279.855 182.935 284.825 186.911 289.795 189.893C296.753 193.869 303.711 194.863 311.663 193.869C329.555 191.881 344.465 182.935 356.393 168.026C359.375 164.05 362.357 160.074 365.339 155.104C366.333 154.111 368.321 151.129 368.321 150.135C371.303 186.911 372.297 200.827 385.219 248.537C387.207 255.494 400.129 258.476 405.099 253.506C412.057 246.549 408.081 237.603 407.087 229.651C402.117 195.857 399.135 162.062 399.135 128.268C419.015 134.231 439.889 122.304 453.805 109.382C468.715 96.461 478.655 80.5577 483.625 62.6664ZM194.371 131.25C192.383 118.328 190.395 97.4549 192.383 77.5758C194.371 59.6846 199.341 48.751 202.323 47.7571C206.299 47.7571 207.293 53.7208 207.293 56.7027C209.281 62.6664 210.275 68.6302 210.275 74.5939C210.275 88.5093 207.293 107.395 201.329 120.316C199.341 124.292 197.353 127.274 194.371 131.25ZM293.771 59.6846C295.759 50.7389 300.729 31.8537 309.675 31.8537C316.633 31.8537 315.639 47.7571 315.639 50.7389C314.645 63.6604 312.657 76.5818 306.693 88.5093C303.711 95.467 298.741 101.431 291.783 105.407C290.789 91.4912 290.789 73.6 293.771 59.6846ZM449.829 62.6664C445.853 70.6181 439.889 80.5577 430.943 88.5093C414.045 102.425 406.093 98.4489 405.099 97.4549C404.105 96.461 405.099 93.4791 405.099 92.4852C408.081 76.5818 418.021 61.6725 428.955 48.751C431.937 44.7752 446.847 27.8779 454.799 32.8477C460.763 35.8296 454.799 52.7268 449.829 62.6664ZM58.1929 165.044C54.2169 161.068 50.2409 156.098 47.2589 152.123C39.3069 143.177 32.3489 132.243 30.3609 120.316C28.3729 109.382 32.3489 99.4429 41.2949 92.4852C48.2529 86.5214 55.2109 80.5577 50.2409 73.6C46.2649 68.6302 39.3069 67.6362 26.3849 73.6C15.4509 78.5697 -3.43511 93.4791 0.540893 130.256C3.52289 154.111 20.4209 174.984 36.3249 190.887C58.1929 213.748 67.1389 236.609 66.1449 255.494C65.1509 266.428 60.1809 275.373 53.2229 284.319C50.2409 288.295 46.2649 292.271 42.2889 296.246C39.3069 299.228 35.3309 302.21 32.3489 306.186C27.3789 313.144 29.3669 323.083 37.3189 327.059C44.2769 330.041 52.2289 329.047 59.1869 325.071C76.0849 316.126 87.0189 302.21 93.9769 283.325C100.935 264.44 100.935 243.567 94.9709 224.682C89.0069 201.821 74.0969 182.935 58.1929 165.044Z" fill="#c88346"></path>
        <path d="M235.125 250.524C234.131 249.531 232.143 249.531 231.149 249.531C218.227 247.543 207.293 249.531 195.365 251.518C192.383 252.512 161.569 259.47 160.575 258.476C151.629 214.742 143.677 173.99 140.695 158.086C137.713 144.171 135.725 130.256 132.743 116.34C130.755 103.419 127.773 90.4973 124.791 77.5758C122.803 69.6241 119.821 61.6725 114.851 55.7087C112.863 52.7269 108.887 50.7389 105.905 51.7329C102.923 51.7329 96.9589 55.7087 96.9589 58.6906C96.9589 64.6544 97.9529 70.6181 98.9469 76.5819L101.929 101.431C104.911 121.31 106.899 141.189 109.881 161.068L118.827 223.688C120.815 239.591 123.797 255.494 126.779 271.398C127.773 277.361 128.767 284.319 131.749 290.283C132.743 293.265 134.731 296.247 138.707 297.24C142.683 298.234 169.521 291.277 179.461 289.289C189.401 287.301 229.161 282.331 236.119 278.355C242.083 275.373 243.077 269.41 243.077 262.452C241.089 255.494 239.101 252.512 235.125 250.524ZM607.875 15.9504C601.911 8.99272 588.989 9.98668 583.025 24.896C569.109 60.6785 554.199 83.5396 538.295 94.4731C537.301 95.4671 534.319 98.4489 533.325 94.4731V88.5093C533.325 84.5335 534.319 81.5516 534.319 77.5758C536.307 65.6483 537.301 54.7148 537.301 43.7812C537.301 33.8417 538.295 23.9021 536.307 13.9625C535.313 7.99876 531.337 0.0471012 524.379 3.02897C516.427 6.01085 514.439 17.9383 512.451 25.89C509.469 35.8296 508.475 45.7692 506.487 55.7087C505.493 65.6483 504.499 76.5819 504.499 86.5214C504.499 96.461 503.505 106.401 506.487 115.346C507.481 118.328 509.469 121.31 511.457 123.298C513.445 125.286 516.427 127.274 519.409 128.268C525.373 130.256 533.325 130.256 539.289 128.268C554.199 124.292 565.133 110.376 574.079 99.4429L575.073 98.4489C556.187 178.96 530.343 226.669 505.493 221.7C497.541 219.712 492.571 210.766 491.577 203.808C489.589 194.863 488.595 185.917 485.613 176.972C483.625 171.008 477.661 160.074 466.727 165.044C456.787 169.02 457.781 182.935 458.775 195.857C459.769 211.76 468.715 251.518 503.505 254.5C539.289 257.482 568.115 235.615 588.989 158.086C597.935 126.28 610.857 63.6604 611.851 29.8658C611.851 26.884 611.851 19.9263 607.875 15.9504ZM802.699 41.7933C789.777 40.7994 781.825 52.7269 776.855 63.6604C748.029 132.243 714.233 195.857 692.365 265.434C690.377 272.392 693.359 281.337 700.317 283.325C712.245 288.295 719.203 275.373 722.185 267.422C733.119 238.597 744.053 210.766 755.981 183.929C765.921 186.911 774.867 188.899 784.807 191.881C780.831 223.688 776.855 248.537 773.873 289.289C772.879 299.228 782.819 310.162 793.753 304.198C800.711 300.222 801.705 289.289 802.699 281.337C812.639 208.778 820.591 138.207 822.579 64.6544C823.573 53.7208 815.621 42.7873 802.699 41.7933ZM795.741 88.5093C795.741 87.5154 795.741 87.5154 795.741 88.5093H796.735V89.5033C792.759 114.352 789.777 139.201 786.795 164.05L766.915 158.086C776.855 135.225 785.801 112.364 795.741 88.5093ZM985.595 106.401C961.739 95.4671 942.853 87.5154 918.003 78.5698C906.075 74.5939 894.147 70.6181 882.219 67.6362C873.273 65.6483 860.351 61.6725 854.387 69.6241C846.435 78.5698 856.375 90.4973 865.321 94.4731C876.255 98.4489 886.195 101.431 895.141 104.413C888.183 122.304 883.213 140.195 878.243 158.086C862.339 211.76 842.459 251.518 828.543 305.192C825.561 319.108 834.507 325.071 840.471 324.077C850.411 322.089 856.375 306.186 863.333 287.301C885.201 226.669 909.057 174.984 923.967 132.243C925.955 126.28 927.943 121.31 929.931 115.346C941.859 119.322 954.781 123.298 968.697 128.268C993.547 138.207 1003.49 114.352 985.595 106.401ZM681.431 225.676C677.455 223.688 672.485 222.694 667.515 224.682C663.539 225.676 659.563 227.663 655.587 227.663C650.617 227.663 645.647 224.682 644.653 218.718C642.665 212.754 643.659 206.79 643.659 200.827C644.653 174.984 649.623 149.141 656.581 124.292C659.563 112.364 663.539 100.437 667.515 89.5033C669.503 83.5396 672.485 77.5758 675.467 71.6121C678.449 66.6423 681.431 59.6846 687.395 56.7027C693.359 53.7208 701.311 56.7027 705.287 64.6544C708.269 70.6181 708.269 76.5819 713.239 81.5516C718.209 87.5154 730.137 89.5033 737.095 78.5698C743.059 68.6302 737.095 54.7148 731.131 45.7692C726.161 37.8175 719.203 29.8658 709.263 25.89C699.323 21.9142 686.401 23.9021 676.461 27.8779C666.521 31.8538 657.575 38.8115 651.611 48.751C643.659 59.6846 638.689 70.6181 633.719 82.5456C628.749 94.4731 624.773 106.401 620.797 118.328C616.821 130.256 613.839 143.177 611.851 156.098C609.863 170.014 608.869 183.929 609.863 197.845C610.857 210.766 612.845 222.694 618.809 234.621C623.779 244.561 632.725 251.518 643.659 255.494C652.605 258.476 664.533 259.47 673.479 255.494C680.437 252.512 690.377 244.561 689.383 235.615C688.389 230.645 685.407 227.663 681.431 225.676Z" fill="#c88346"></path>
      </svg>
      <div style="font-size:16px;font-weight:bold;color:#3d2b1f;letter-spacing:1px;margin-top:4px">Sleep Management Pvt. Ltd.</div>
    </td>
  </tr>

  <!-- Row 2: PROFORMA INVOICE title -->
  <tr>
    <td colspan="6" style="text-align:center;padding:8px 10px;border-bottom:2px solid #333;background:#f9f9f9">
      <div style="font-size:16px;font-weight:bold;letter-spacing:2px">TAX INVOICE</div>
    </td>
  </tr>

  <!-- Row 3: Company info | Invoice # | Date -->
  <tr>
    <td colspan="3" style="padding:10px;border-bottom:1px solid #bbb;border-right:1px solid #bbb;width:50%">
      <div style="font-size:11px;line-height:1.6">
        <strong>${companyName}</strong><br>
        ${storeLoc.name || locationName ? (storeLoc.name || locationName) + '<br>' : ''}
        ${storeAddress ? storeAddress.replace(/\n/g, '<br>') + '<br>' : ''}
        ${storePhone ? 'Phone : ' + storePhone + '<br>' : ''}
        ${storeGST ? 'GSTIN : ' + storeGST : ''}
      </div>
    </td>
    <td colspan="3" style="padding:10px;border-bottom:1px solid #bbb;width:50%">
      <table style="width:100%;font-size:11px;border-collapse:collapse">
        <tr><td style="padding:3px 0;font-weight:bold">Invoice No:</td><td style="padding:3px 0;text-align:right">${selfInvoiceNumber || o.name || '#' + o.orderNumber}</td></tr>
        ${selfInvoiceNumber ? `<tr><td style="padding:3px 0;font-weight:bold">Order No:</td><td style="padding:3px 0;text-align:right">${o.name || '#' + o.orderNumber}</td></tr>` : ''}
        <tr><td style="padding:3px 0;font-weight:bold">Invoice Date:</td><td style="padding:3px 0;text-align:right">${invoiceDate}</td></tr>
        <tr><td style="padding:3px 0;font-weight:bold">Print Time:</td><td style="padding:3px 0;text-align:right">${printTime}</td></tr>
        <tr><td style="padding:3px 0;font-weight:bold">Status:</td><td style="padding:3px 0;text-align:right">${o.cancelledAt ? 'Cancelled' : (o.financialStatus || 'Pending')}</td></tr>
        ${locationName ? `<tr><td style="padding:3px 0;font-weight:bold">Location:</td><td style="padding:3px 0;text-align:right">${locationName}</td></tr>` : ''}
      </table>
    </td>
  </tr>

  <!-- Row 4: Sold By | Invoice To -->
  <tr>
    <td colspan="3" style="padding:0;border-bottom:1px solid #bbb;border-right:1px solid #bbb;width:50%">
      <div class="section-header">${sellerBlockLabel}</div>
      <div style="padding:8px 10px">${sellerBlock}</div>
    </td>
    <td colspan="3" style="padding:0;border-bottom:1px solid #bbb;width:50%">
      <div class="section-header">Bill to / Buyer</div>
      <div style="padding:8px 10px">${invoiceToBlock}</div>
    </td>
  </tr>

  <!-- Row 5: Product table header -->
  <tr>
    <td colspan="6" style="padding:0;border-bottom:1px solid #bbb">
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:#f0f0f0">
            <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:center;width:25px">#</th>
            <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:left;min-width:160px">Product</th>
            <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:center;width:80px;white-space:nowrap">HSN</th>
            <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:right;width:70px">Rate (₹)</th>
            <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:center;width:30px">Qty</th>
            <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:right;width:65px">Disc (₹)</th>
            <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:right;width:75px">Taxable (₹)</th>
            ${orderInterState
              ? `<th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:center;width:40px">IGST%</th>
                 <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:right;width:60px">IGST (₹)</th>`
              : `<th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:center;width:40px">CGST%</th>
                 <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:right;width:60px">CGST (₹)</th>
                 <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:center;width:40px">SGST%</th>
                 <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:right;width:60px">SGST (₹)</th>`}
            <th style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:right;width:75px">Total (₹)</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHtml}
        </tbody>
      </table>
    </td>
  </tr>

  <!-- Row 6: Total items/qty -->
  <tr>
    <td colspan="6" style="padding:8px 10px;border-bottom:1px solid #bbb;font-size:11px;font-weight:bold">
      Total Items / Qty: ${(o.lineItems || []).length} Item(s), ${totalQty} Qty
    </td>
  </tr>

  <!-- Row 7: Amount in words -->
  <tr>
    <td colspan="6" style="padding:8px 10px;border-bottom:1px solid #bbb;font-size:11px">
      <strong>Amount in words:</strong> ${numberToWords(total)}
    </td>
  </tr>

  <!-- Row 8: Payment | Summary -->
  <tr>
    <td colspan="3" style="padding:0;border-right:1px solid #bbb;vertical-align:top">
      <div class="section-header">Payment Details</div>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <tr style="background:#f5f5f5"><th style="padding:5px 10px;text-align:left;border-bottom:1px solid #ddd">Method</th><th style="padding:5px 10px;text-align:right;border-bottom:1px solid #ddd">Amount</th></tr>
        ${paymentRows.map(r => `<tr><td style="padding:5px 10px;border-bottom:1px solid #eee">${r.method}</td><td style="padding:5px 10px;text-align:right;border-bottom:1px solid #eee">${r.amount}</td></tr>`).join('')}
        <tr><td colspan="2" style="padding:5px 10px;font-size:10px;color:#666;border-bottom:1px solid #eee">Ref No. ${o.name || '#' + o.orderNumber}</td></tr>
      </table>
    </td>
    <td colspan="3" style="padding:0;vertical-align:top">
      <div class="section-header">Order Summary</div>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <tr><td style="padding:5px 10px;border-bottom:1px solid #eee">Subtotal</td><td style="padding:5px 10px;text-align:right;border-bottom:1px solid #eee">₹${grossTotal.toFixed(2)}</td></tr>
        ${totalDiscounts > 0 ? `<tr><td style="padding:5px 10px;border-bottom:1px solid #eee;color:#dc2626">Discount</td><td style="padding:5px 10px;text-align:right;border-bottom:1px solid #eee;color:#dc2626">-₹${totalDiscounts.toFixed(2)}</td></tr>` : ''}
        <tr><td style="padding:5px 10px;border-bottom:1px solid #eee">Taxable Value</td><td style="padding:5px 10px;text-align:right;border-bottom:1px solid #eee">₹${totalTaxableAmt.toFixed(2)}</td></tr>
        ${orderInterState
          ? `<tr><td style="padding:5px 10px;border-bottom:1px solid #eee">IGST</td><td style="padding:5px 10px;text-align:right;border-bottom:1px solid #eee">₹${totalIGST.toFixed(2)}</td></tr>`
          : `<tr><td style="padding:5px 10px;border-bottom:1px solid #eee">CGST</td><td style="padding:5px 10px;text-align:right;border-bottom:1px solid #eee">₹${totalCGST.toFixed(2)}</td></tr>
             <tr><td style="padding:5px 10px;border-bottom:1px solid #eee">SGST</td><td style="padding:5px 10px;text-align:right;border-bottom:1px solid #eee">₹${totalSGST.toFixed(2)}</td></tr>`}
        ${rounding !== 0 ? `<tr><td style="padding:5px 10px;border-bottom:1px solid #eee">Rounding</td><td style="padding:5px 10px;text-align:right;border-bottom:1px solid #eee">₹${rounding.toFixed(2)}</td></tr>` : ''}
        <tr style="font-weight:bold;font-size:13px;background:#f9f9f9"><td style="padding:8px 10px;border-top:2px solid #333">Total</td><td style="padding:8px 10px;text-align:right;border-top:2px solid #333">₹${total.toFixed(2)}</td></tr>
      </table>
    </td>
  </tr>

  <!-- Footer -->
  <tr>
    <td colspan="6" style="padding:12px 10px;text-align:center;border-top:1px solid #bbb;font-size:10px;color:#888">
      This is a computer-generated tax invoice and does not require a signature.<br>
      Thank you for shopping with SleepyCat! | info@sleepycat.in
    </td>
  </tr>

</table>

${invoiceActionsBar(`Tax-Invoice-${(selfInvoiceNumber || o.name || o.orderNumber || 'order').toString().replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`, o.id, 'tax')}
${termsAndConditionsHtml}
</body></html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=1100');
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading orders...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '14px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(44,32,23,0.06)',
        border: '1px solid rgba(200, 131, 70, 0.12)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#3d2b1f' }}>
            Recent Orders
          </h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleExportCSV}
              disabled={filteredOrders.length === 0}
              style={{
                padding: '10px 20px',
                background: filteredOrders.length === 0 ? '#e5e7eb' : 'white',
                color: filteredOrders.length === 0 ? '#9ca3af' : '#c88346',
                border: '2px solid ' + (filteredOrders.length === 0 ? '#e5e7eb' : '#c88346'),
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: filteredOrders.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Export CSV
            </button>
            <button
              onClick={fetchOrders}
              style={{
                padding: '10px 20px',
                background: '#c88346',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Date filter */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Preset buttons */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {DATE_PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                style={{
                  padding: '7px 14px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  border: activePreset === p.key ? '2px solid #c88346' : '2px solid rgba(200,131,70,0.2)',
                  background: activePreset === p.key ? '#c88346' : 'white',
                  color: activePreset === p.key ? 'white' : '#c88346',
                  transition: 'all 0.15s'
                }}
              >{p.label}</button>
            ))}
          </div>

          {/* Divider */}
          <span style={{ color: '#d1c4b8', fontWeight: 300 }}>|</span>

          {/* Custom date range */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '13px', color: '#6b5745', fontWeight: 600, whiteSpace: 'nowrap' }}>From</label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={e => { setDateFrom(e.target.value); setActivePreset(''); }}
              style={{
                padding: '7px 10px', borderRadius: '8px', fontSize: '13px',
                border: '2px solid rgba(200,131,70,0.2)', outline: 'none', cursor: 'pointer'
              }}
              onFocus={e => e.target.style.borderColor = '#c88346'}
              onBlur={e => e.target.style.borderColor = 'rgba(200,131,70,0.2)'}
            />
            <label style={{ fontSize: '13px', color: '#6b5745', fontWeight: 600, whiteSpace: 'nowrap' }}>To</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              max={todayStr()}
              onChange={e => { setDateTo(e.target.value); setActivePreset(''); }}
              style={{
                padding: '7px 10px', borderRadius: '8px', fontSize: '13px',
                border: '2px solid rgba(200,131,70,0.2)', outline: 'none', cursor: 'pointer'
              }}
              onFocus={e => e.target.style.borderColor = '#c88346'}
              onBlur={e => e.target.style.borderColor = 'rgba(200,131,70,0.2)'}
            />
          </div>

          {/* Order count badge */}
          {!loading && (
            <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#6b5745', fontWeight: 600,
              background: 'rgba(200,131,70,0.1)', padding: '5px 12px', borderRadius: '20px' }}>
              {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Truncation warning — server hit its fetch cap for this date range */}
        {!loading && truncated && (
          <div style={{
            marginBottom: '16px', padding: '10px 14px', borderRadius: '10px',
            background: '#fef3cd', border: '1px solid #f0d48a', color: '#7a5c1e', fontSize: '13px', fontWeight: 600
          }}>
            ⚠️ This date range has more orders than can be shown at once — only the most recent are listed. Narrow the date range to see the rest.
          </div>
        )}

        {/* Search + Page Size */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '16px', pointerEvents: 'none' }}>&#128269;</span>
            <input
              type="text"
              placeholder="Search by order #, customer name, email, status..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                width: '100%',
                padding: '10px 14px 10px 36px',
                border: '2px solid rgba(200, 131, 70, 0.12)',
                borderRadius: '10px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#c88346'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(200, 131, 70, 0.12)'}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>Show</span>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              style={{
                padding: '10px 12px',
                border: '2px solid rgba(200, 131, 70, 0.12)',
                borderRadius: '10px',
                fontSize: '13px',
                color: '#3d2b1f',
                background: '#ffffff',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        {!loading && (
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>
            {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}{searchQuery.length >= 2 ? ' found' : ''}
            {filteredOrders.length > pageSize && ` — showing ${startIdx + 1}–${Math.min(startIdx + pageSize, filteredOrders.length)}`}
          </div>
        )}

        {orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            No orders found
          </div>
        ) : filteredOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            No orders match your search
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(200, 131, 70, 0.12)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                    Order
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                    Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                    Customer
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                    Payment
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                    Order Status
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                    Type
                  </th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                    Total
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order) => (
                  <tr key={order.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '16px', fontSize: '14px', fontWeight: '600', color: '#3d2b1f' }}>
                      {order.name || `#${order.orderNumber}`}
                      {(() => { const inv = (order.noteAttributes || []).find(a => a.name === 'Invoice Number'); return inv ? <div style={{ fontSize: '11px', fontWeight: '500', color: '#7c3aed', marginTop: '2px' }}>{inv.value}</div> : null; })()}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#7a6b5d' }}>
                      {formatDate(order.createdAt)}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#3d2b1f' }}>
                      {order.customer ? order.customer.name : 'Guest'}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {(() => {
                        const status = order.cancelledAt && order.financialStatus === 'paid' ? 'voided' : order.financialStatus;
                        return <span style={{
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: status === 'paid' ? '#eafaf1' : status === 'refunded' ? '#fef3e2' : status === 'voided' ? '#f3f4f6' : '#fce8e6',
                          color: status === 'paid' ? '#1e7e34' : status === 'refunded' ? '#92400e' : status === 'voided' ? '#6b7280' : '#a5311f'
                        }}>
                          {status}
                        </span>;
                      })()}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: order.cancelledAt ? '#fce8e6' : order.fulfillmentStatus === 'fulfilled' ? '#eafaf1' : order.fulfillmentStatus === 'partial' ? '#fef3e2' : '#f3f4f6',
                          color: order.cancelledAt ? '#a5311f' : order.fulfillmentStatus === 'fulfilled' ? '#1e7e34' : order.fulfillmentStatus === 'partial' ? '#92400e' : '#6b7280'
                        }}>
                          {order.cancelledAt ? 'Cancelled' : order.fulfillmentStatus || 'Unfulfilled'}
                        </span>
                        {order.cancelReason && (
                          <span style={{ fontSize: '11px', color: '#a5311f', fontWeight: '500' }}>
                            {order.cancelReason}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {(() => {
                        const isWarehouse = (order.tags || '').toLowerCase().includes('warehouse');
                        return <span style={{
                          padding: '4px 10px',
                          borderRadius: '16px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: isWarehouse ? '#ede9fe' : '#eafaf1',
                          color: isWarehouse ? '#6d28d9' : '#1e7e34'
                        }}>
                          {isWarehouse ? 'Warehouse' : 'Store'}
                        </span>;
                      })()}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', fontWeight: '600', color: '#3d2b1f', textAlign: 'right' }}>
                      {formatCurrency(order.total)}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <button
                        onClick={() => viewOrderDetails(order.id)}
                        style={{
                          padding: '6px 16px',
                          background: '#f5f0eb',
                          color: '#c88346',
                          border: '1px solid rgba(200, 131, 70, 0.12)',
                          borderRadius: '10px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(200, 131, 70, 0.12)' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 10px', border: '1px solid rgba(200, 131, 70, 0.12)', borderRadius: '10px',
                    background: currentPage === 1 ? '#f3f4f6' : 'white', color: currentPage === 1 ? '#9ca3af' : '#3d2b1f',
                    fontSize: '13px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  &laquo;
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px', border: '1px solid rgba(200, 131, 70, 0.12)', borderRadius: '10px',
                    background: currentPage === 1 ? '#f3f4f6' : 'white', color: currentPage === 1 ? '#9ca3af' : '#3d2b1f',
                    fontSize: '13px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Prev
                </button>
                <span style={{ fontSize: '13px', color: '#374151', fontWeight: '600', padding: '0 8px' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 12px', border: '1px solid rgba(200, 131, 70, 0.12)', borderRadius: '10px',
                    background: currentPage === totalPages ? '#f3f4f6' : 'white', color: currentPage === totalPages ? '#9ca3af' : '#3d2b1f',
                    fontSize: '13px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 10px', border: '1px solid rgba(200, 131, 70, 0.12)', borderRadius: '10px',
                    background: currentPage === totalPages ? '#f3f4f6' : 'white', color: currentPage === totalPages ? '#9ca3af' : '#3d2b1f',
                    fontSize: '13px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  &raquo;
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (() => {
        const HIDDEN_ATTRS = new Set([
          'ezetap txnid', 'ezetap p2p request id', 'ezetap ref number',
          'ezetap receipt url', 'ezetap payment mode', 'pos custom discount',
          'customergstin', 'invoice number'
        ]);
        const visibleAttrs = (selectedOrder.noteAttributes || []).filter(a => !HIDDEN_ATTRS.has(a.name.toLowerCase()));
        const paymentMethod = visibleAttrs.find(a => a.name === 'Payment Method')?.value || (selectedOrder.paymentGateways || []).join(', ') || '-';
        const paymentAmount = visibleAttrs.find(a => a.name === 'Payment Amount')?.value || '';
        const transactionId = visibleAttrs.find(a => a.name === 'Transaction ID')?.value || '';
        const staffName = visibleAttrs.find(a => a.name === 'Staff Name')?.value || '';
        const gstCompany = visibleAttrs.find(a => a.name === 'GST Company Name')?.value || '';
        const gstin = visibleAttrs.find(a => a.name === 'GSTIN')?.value || '';
        const financialStatus = selectedOrder.cancelledAt && selectedOrder.financialStatus === 'paid' ? 'voided' : selectedOrder.financialStatus;
        const orderStatus = selectedOrder.cancelledAt ? 'Cancelled' : selectedOrder.fulfillmentStatus || 'Unfulfilled';
        const statusColor = (s) => s === 'paid' ? { bg: '#eafaf1', fg: '#1e7e34' } : s === 'refunded' ? { bg: '#fef3e2', fg: '#92400e' } : s === 'voided' ? { bg: '#f3f4f6', fg: '#6b7280' } : { bg: '#fce8e6', fg: '#a5311f' };
        const orderColor = selectedOrder.cancelledAt ? { bg: '#fce8e6', fg: '#a5311f' } : orderStatus === 'fulfilled' ? { bg: '#eafaf1', fg: '#1e7e34' } : orderStatus === 'partial' ? { bg: '#fef3e2', fg: '#92400e' } : { bg: '#f3f4f6', fg: '#6b7280' };
        const fsc = statusColor(financialStatus);

        return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 15, 30, 0.55)', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '28px',
            maxWidth: '720px',
            width: '92%',
            maxHeight: '85vh',
            overflow: 'auto'
          }}>
            {/* ─── Header ─── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#3d2b1f', margin: 0 }}>
                  Order {selectedOrder.name || `#${selectedOrder.orderNumber}`}
                </h3>
                {(() => { const inv = (selectedOrder.noteAttributes || []).find(a => a.name === 'Invoice Number'); return inv ? <div style={{ fontSize: '13px', fontWeight: '600', color: '#7c3aed', marginTop: '2px' }}>{inv.value}</div> : null; })()}
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                  {formatDate(selectedOrder.createdAt)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {!selectedOrder.cancelledAt && (
                <button
                  onClick={handlePrintReceipt}
                  style={{
                    padding: '7px 16px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}
                >
                  🖨️ Print Receipt
                </button>
                )}
                <button
                  onClick={() => setSelectedOrder(null)}
                  style={{
                    padding: '7px 14px',
                    background: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#374151'
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            {/* ─── Status Badges ─── */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: fsc.bg, color: fsc.fg }}>{financialStatus}</span>
              <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: orderColor.bg, color: orderColor.fg }}>{orderStatus}</span>
            </div>

            {/* ─── Cancel Reason ─── */}
            {selectedOrder.cancelReason && (() => {
              let displayReason = selectedOrder.cancelReason;
              if (selectedOrder.cancelReason === 'other' && selectedOrder.note) {
                const match = selectedOrder.note.match(/Reason:\s*Other:\s*(.+)/i);
                if (match) displayReason = match[1].trim();
              }
              const reasonLabels = { customer: 'Customer request', fraud: 'Fraudulent order', inventory: 'Out of stock / Inventory', declined: 'Payment declined' };
              return (
                <div style={{ background: '#fce8e6', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#a5311f' }}>
                  <strong>Cancelled:</strong> {reasonLabels[displayReason] || displayReason}
                </div>
              );
            })()}

            {/* ─── Two-Column: Order Info + Payment ─── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
              {/* Left: Payment & Staff */}
              <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '700', marginBottom: '10px', letterSpacing: '0.5px' }}>Payment</div>
                <div style={{ fontSize: '13px', color: '#374151', marginBottom: '6px' }}>
                  <span style={{ color: '#9ca3af' }}>Method</span>
                  <span style={{ float: 'right', fontWeight: '600' }}>{paymentMethod}</span>
                </div>
                {paymentAmount && (
                  <div style={{ fontSize: '13px', color: '#374151', marginBottom: '6px' }}>
                    <span style={{ color: '#9ca3af' }}>Amount</span>
                    {/* Show the actual order total (matches the Shopify order + invoice PDF) rather
                        than the stored "Payment Amount" note attribute — older orders baked in a
                        pre-cascade value that overstated the discount. */}
                    <span style={{ float: 'right', fontWeight: '600' }}>{formatCurrency(selectedOrder.total)}</span>
                  </div>
                )}
                {transactionId && (
                  <div style={{ fontSize: '12px', color: '#374151', marginBottom: '6px', wordBreak: 'break-all' }}>
                    <span style={{ color: '#9ca3af' }}>Txn ID</span>
                    <span style={{ float: 'right', fontWeight: '600', fontFamily: 'monospace', fontSize: '11px' }}>{transactionId}</span>
                  </div>
                )}
                {staffName && (
                  <div style={{ fontSize: '13px', color: '#374151', borderTop: '1px solid #e5e7eb', paddingTop: '6px', marginTop: '6px' }}>
                    <span style={{ color: '#9ca3af' }}>Staff</span>
                    <span style={{ float: 'right', fontWeight: '600' }}>{staffName}</span>
                  </div>
                )}
              </div>

              {/* Right: Customer / GST */}
              <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '700', marginBottom: '10px', letterSpacing: '0.5px' }}>Customer</div>
                {selectedOrder.customer && (
                  <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px', fontWeight: '600' }}>
                    {selectedOrder.customer.first_name ? `${selectedOrder.customer.first_name} ${selectedOrder.customer.last_name || ''}`.trim() : selectedOrder.customer.name || 'Walk-in'}
                  </div>
                )}
                {selectedOrder.customer?.phone && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>{selectedOrder.customer.phone}</div>
                )}
                {selectedOrder.customer?.email && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>{selectedOrder.customer.email}</div>
                )}
                {(gstin || gstCompany) && (
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '6px', marginTop: '4px' }}>
                    {gstCompany && <div style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>{gstCompany}</div>}
                    {gstin && <div style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>{gstin}</div>}
                  </div>
                )}
              </div>
            </div>

            {/* ─── Items ─── */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '700', marginBottom: '10px', letterSpacing: '0.5px' }}>
                Items ({selectedOrder.lineItems.length})
              </div>
              {selectedOrder.lineItems.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  padding: '10px 14px',
                  background: index % 2 === 0 ? '#f9fafb' : '#ffffff',
                  borderRadius: '8px',
                  marginBottom: '4px'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                      {item.title}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '2px' }}>
                      {item.variant_title && item.variant_title !== 'Default Title' && (
                        <span style={{ fontSize: '11px', color: '#6b7280' }}>{item.variant_title}</span>
                      )}
                      {item.sku && (
                        <span style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{item.sku}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                      {formatCurrency(item.price * item.quantity)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                      {item.quantity > 1 ? `${item.quantity} × ${formatCurrency(item.price)}` : `Qty: ${item.quantity}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ─── Totals ─── */}
            <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Subtotal</span>
                {/* GROSS subtotal (sum of price × qty), same as the invoice PDF — so
                    Subtotal − Discount = Total. selectedOrder.subtotal is Shopify's already-net
                    subtotal_price, which double-counts the discount line below it. */}
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{formatCurrency((selectedOrder.lineItems || []).reduce((s, li) => s + parseFloat(li.price || 0) * li.quantity, 0))}</span>
              </div>
              {parseFloat(selectedOrder.totalDiscounts || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#dc2626' }}>Discount</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#dc2626' }}>-{formatCurrency(selectedOrder.totalDiscounts)}</span>
                </div>
              )}
              {parseFloat(selectedOrder.totalTax || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Tax</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{formatCurrency(selectedOrder.totalTax)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>Total</span>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>{formatCurrency(selectedOrder.total)}</span>
              </div>
            </div>

            {/* ─── Dispatch / Fulfillment ─── */}
            {selectedOrder.fulfillments && selectedOrder.fulfillments.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '700', marginBottom: '10px', letterSpacing: '0.5px' }}>Dispatch</div>
                {selectedOrder.fulfillments.map((f, fi) => (
                  <div key={fi} style={{
                    background: f.status === 'cancelled' ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', padding: '12px',
                    marginBottom: '6px', fontSize: '13px', color: f.status === 'cancelled' ? '#991b1b' : '#166534'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>{f.status}</span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>{formatDate(f.createdAt)}</span>
                    </div>
                    {f.trackingCompany && (
                      <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                        {f.trackingCompany}{f.trackingNumber ? ` — ` : ''}
                        {f.trackingNumber && <span style={{ fontFamily: 'monospace' }}>{f.trackingNumber}</span>}
                      </div>
                    )}
                    {f.trackingNumber && (
                      <a href={`https://track.sleepycat.in/en?waybill=${f.trackingNumber}`} target="_blank" rel="noopener noreferrer" style={{ color: '#c88346', fontWeight: '600', textDecoration: 'underline', fontSize: '12px' }}>
                        Track Shipment
                      </a>
                    )}
                    {f.lineItems && f.lineItems.length > 0 && (
                      <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: `1px solid ${f.status === 'cancelled' ? '#fecaca' : '#bbf0c9'}`, fontSize: '12px', color: '#374151' }}>
                        {f.lineItems.map((li, li_idx) => (
                          <div key={li_idx}>{li.title} × {li.quantity}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Cancel Order Button — DISABLED, will activate later */}
            {false && !selectedOrder.cancelledAt && (() => {
              const cancellable = isCancellable(selectedOrder);
              const timeLeft = getCancelTimeRemaining(selectedOrder);
              const paymentMethod = getPaymentMethod(selectedOrder);
              const refundInfo = getRefundInfo(paymentMethod);

              return (
                <div style={{ marginTop: '20px' }}>
                  {/* Cancellation time indicator */}
                  {!selectedOrder.cancelledAt && (
                    <div style={{
                      fontSize: '12px',
                      color: cancellable ? '#16a34a' : '#9ca3af',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        background: cancellable ? '#16a34a' : '#d1d5db',
                        display: 'inline-block'
                      }} />
                      {cancellable
                        ? `Cancellation available — ${timeLeft} remaining`
                        : 'Cancellation window expired (10 minutes)'}
                    </div>
                  )}

                  {/* Cancel Result Message */}
                  {cancelResult && (
                    <div style={{
                      background: cancelResult.success ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${cancelResult.success ? '#bbf7d0' : '#fecaca'}`,
                      borderRadius: '10px',
                      padding: '14px',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: cancelResult.success ? '#166534' : '#dc2626',
                        marginBottom: cancelResult.refundMessage ? '8px' : 0
                      }}>
                        {cancelResult.message}
                      </div>
                      {cancelResult.refundMessage && (
                        <div style={{
                          fontSize: '13px',
                          color: cancelResult.refundType === 'manual' ? '#dc2626' : cancelResult.refundType === 'pending_api' ? '#d97706' : '#166534',
                          background: '#ffffff',
                          padding: '10px 12px',
                          borderRadius: '10px',
                          marginTop: '6px',
                          fontWeight: '500'
                        }}>
                          {cancelResult.refundType === 'manual' && '⚠️ '}
                          {cancelResult.refundType === 'pending_api' && '🕐 '}
                          {cancelResult.refundType === 'auto' && '✅ '}
                          {cancelResult.refundMessage}
                        </div>
                      )}
                      {cancelResult.cancelledFulfillments > 0 && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                          {cancelResult.cancelledFulfillments} fulfillment(s) cancelled
                        </div>
                      )}
                      <button
                        onClick={() => { setCancelResult(null); setShowCancelConfirm(false); setSelectedOrder(null); }}
                        style={{
                          marginTop: '10px', padding: '8px 16px', background: '#f3f4f6', color: '#374151',
                          border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                        }}
                      >
                        Close
                      </button>
                    </div>
                  )}

                  {/* Cancel button / form */}
                  {!cancelResult && cancellable && (
                    <>
                      {!showCancelConfirm ? (
                        <button
                          onClick={() => { setShowCancelConfirm(true); setCancelReason('customer'); setOtherReasonText(''); setCancelResult(null); }}
                          style={{
                            width: '100%', padding: '12px', background: '#fee2e2', color: '#dc2626',
                            border: '1px solid #fecaca', borderRadius: '10px', fontSize: '14px',
                            fontWeight: '600', cursor: 'pointer'
                          }}
                        >
                          Cancel Order
                        </button>
                      ) : (
                        <div style={{
                          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px',
                          padding: '16px'
                        }}>
                          <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#dc2626', marginBottom: '12px' }}>
                            Cancel Order {selectedOrder.name || `#${selectedOrder.orderNumber}`}
                          </h4>

                          {/* Payment method & refund info */}
                          {paymentMethod && (
                            <div style={{
                              fontSize: '13px', marginBottom: '12px',
                              background: '#ffffff', padding: '10px 12px', borderRadius: '10px'
                            }}>
                              <div style={{ color: '#374151', marginBottom: '6px' }}>
                                Payment: <strong>{paymentMethod}</strong>
                              </div>
                              <div style={{ color: refundInfo.color, fontWeight: '600', fontSize: '12px' }}>
                                {refundInfo.type === 'manual' && '⚠️ '}
                                {refundInfo.type === 'pending' && '🕐 '}
                                {refundInfo.type === 'auto' && '✅ '}
                                {refundInfo.label}
                              </div>
                            </div>
                          )}

                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                              Reason for cancellation
                            </label>
                            <select
                              value={cancelReason}
                              onChange={e => { setCancelReason(e.target.value); if (e.target.value !== 'other') setOtherReasonText(''); }}
                              style={{
                                width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                                borderRadius: '8px', fontSize: '14px', color: '#374151',
                                background: '#ffffff', outline: 'none', boxSizing: 'border-box'
                              }}
                            >
                              <option value="customer">Customer request</option>
                              <option value="inventory">Out of stock / Inventory</option>
                              <option value="fraud">Fraudulent order</option>
                              <option value="declined">Payment declined</option>
                              <option value="other">Other</option>
                            </select>
                            {cancelReason === 'other' && (
                              <textarea
                                value={otherReasonText}
                                onChange={e => setOtherReasonText(e.target.value)}
                                placeholder="Please specify the reason for cancellation..."
                                rows={3}
                                style={{
                                  width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                                  borderRadius: '8px', fontSize: '14px', color: '#374151',
                                  background: '#ffffff', outline: 'none', boxSizing: 'border-box',
                                  marginTop: '8px', resize: 'vertical', fontFamily: 'inherit'
                                }}
                              />
                            )}
                          </div>

                          <div style={{ fontSize: '12px', color: '#a5311f', marginBottom: '12px' }}>
                            This will cancel the order, void fulfillments, and handle refund of {formatCurrency(selectedOrder.total)}
                          </div>

                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              onClick={handleCancelOrder}
                              disabled={cancelling || (cancelReason === 'other' && !otherReasonText.trim())}
                              style={{
                                flex: 1, padding: '10px', background: (cancelling || (cancelReason === 'other' && !otherReasonText.trim())) ? '#fca5a5' : '#dc2626',
                                color: 'white', border: 'none', borderRadius: '8px',
                                fontSize: '14px', fontWeight: '600',
                                cursor: cancelling ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
                            </button>
                            <button
                              onClick={() => setShowCancelConfirm(false)}
                              disabled={cancelling}
                              style={{
                                padding: '10px 16px', background: '#ffffff', color: '#374151',
                                border: '1px solid #d1d5db', borderRadius: '8px',
                                fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                              }}
                            >
                              Back
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        );
      })()}
    </div>
  );
}

export default Orders;
